import type { TeamMessageRecord } from "../registration/registrationModelV4";
/* ============================================================== */
/*                                                                */
/*   HIS V4 · หน้าตรวจรักษา · DOMAIN MODEL + API MAPPING           */
/*                                                                */
/*   ไฟล์นี้ pure: ไม่ import React / issara-sdk                    */
/*   -> unit test ใน node ได้ และ card ทุกใบ import จากที่นี่ ONLY    */
/*                                                                */
/*   ชื่อ field ทุกตัวอ้างอิงจาก caller จริงใน HIS V3 (file:line)    */
/*   GET  /apis/DPO/medical_record/emr/{emr}/    CardPatientInfo.tsx:265-330  */
/*   POST /apis/DPO/patient_screen/new/          CardPatientInfo.tsx:464-540  */
/*   PUT  /apis/DPO/patient_screen/update/       CardPatientInfo.tsx:464-570  */
/*   GET/PUT /apis/DPO/medical_record/{emr}/diagnosis/  DPOI.ts:176-192       */
/*   POST /apis/core/icd10KeyUp/{method}         ReportPatientList.ts:357-372 */
/*   GET  /apis/DPO/physical_exam_organ/         DPOInterface.ts:90-95        */
/*   GET  /apis/DPO/physical-exam-template/      sequence/PhysicalExam.ts:102 */
/*   GET/POST /apis/DPO/medical_record/gallery/{emr}/    DPOI.ts:496          */
/*   PUT  /apis/DPO/medical_record/picture/{pk}/         DPOI.ts:519          */
/*   GET  /apis/PTM/nurse-note-item/ + /apis/PTM/nurse-note-latest/  PTMInterface.ts:274-292 */
/*   POST /apis/PTM/nurse-note-item/             PTMInterface.ts:515-535      */
/*   GET  /apis/PTM/nurse-note-template-item/    PTMInterface.ts:300-306      */
/*   GET  /apis/core/triage/encounter/{pk}/      sequence/PhysicalExam.ts:208 */
/*   GET  /apis/PTM/vital-sign-value/            sequence/PhysicalExam.ts:230 */
/*                                                                */
/* ============================================================== */

import moment, { Moment } from "moment";

import { formatDate, parseDate } from "react-lib/utils/dateUtils";

import type { CheckoutEncounter } from "./checkoutModelV4";
import { beDateTimeText, TRIAGE_LEVELS, vitalSeverityOf } from "./screenModelV4";

/* โคลน types จาก mockPatientV4 (chip ของ CardV4) ไว้ที่นี่ เพื่อไม่ให้เกิด import วนกลับ
   (mockPatientV4 import type จากไฟล์นี้) */
export type ExamChipTone = "neutral" | "warn" | "danger";
export type ExamSummaryChip = { text: string; tone: ExamChipTone };

/* ============================================================== */
/*   SECTION 1 · PRIMITIVE READ HELPERS (API payload เป็น unknown)  */
/* ============================================================== */

export type Dict = { [key: string]: unknown };

export const asDict = (value: unknown): Dict =>
  value !== null && typeof value === "object" && !Array.isArray(value) ? (value as Dict) : {};

export const asList = (value: unknown): Dict[] =>
  Array.isArray(value) ? (value.filter((item) => item !== null && typeof item === "object") as Dict[]) : [];

/** DRF list payload รับได้ทั้ง { items: [] } และ [] ตรง ๆ */
export const itemsOf = (payload: unknown): Dict[] => {
  if (Array.isArray(payload)) return asList(payload);
  return asList(asDict(payload).items);
};

export const asText = (value: unknown): string => {
  if (typeof value === "string") return value;
  if (typeof value === "number") return `${value}`;
  return "";
};

export const asNumber = (value: unknown): number | undefined => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() !== "" && Number.isFinite(Number(value))) return Number(value);
  return undefined;
};

export const asBoolean = (value: unknown): boolean =>
  value === true || value === 1 || value === "1" || value === "true";

const trimOption = (value: unknown): string => asText(value).trim();

/**
 * V3 decodeHTMLEntities (CardNurseNote.tsx:106) ใช้ document.createElement
 * ที่นี่ทำด้วย regex เพื่อให้รันใน node/test ได้ และผลเท่ากันสำหรับ entity ที่ API ส่งมา
 */
const NAMED_ENTITY: { [name: string]: string } = {
  amp: "&",
  lt: "<",
  gt: ">",
  quot: '"',
  apos: "'",
  nbsp: " ",
  ndash: "–",
  mdash: "—",
  hellip: "…",
  brvbar: "¦",
  bull: "•",
};

export const decodeHtml = (text: unknown): string =>
  asText(text)
    .replace(/&#(\d+);/g, (_m, code) => String.fromCharCode(parseInt(code, 10)))
    .replace(/&#x([0-9a-fA-F]+);/g, (_m, code) => String.fromCharCode(parseInt(code, 16)))
    .replace(/&([a-zA-Z]+);/g, (whole, name) => {
      const decoded = NAMED_ENTITY[name.toLowerCase()];
      return decoded === undefined ? whole : decoded;
    });

/* ============================================================== */
/*   SECTION 2 · encounter / emr identity                         */
/* ============================================================== */

/** id ที่หน้าตรวจรักษาต้องรู้ ก่อนเรียก API ใดๆ */
export type ExamIds = {
  encounterId?: number;
  emrId?: number;
  patientId?: number;
  doctorId?: number | null;
};

export const hasExamIds = (ids: ExamIds): boolean => !!ids.encounterId && !!ids.emrId;

/* ============================================================== */
/*   SECTION 3 · SUBJECTIVE (ประวัติ)                              */
/* ============================================================== */

/**
 * patient_illness  : chief_complaint / present_illness / advice (PatientIllnessSerializer_apps_DPO)
 * patient_history  : past_illness / personal_history / family_history (PatientHistorySerializer_apps_DPO)
 * sensitive_note   : sensitive_note (ClassifiedPatientHistorySerializer_apps_DPO)
 */
export type Subjective = {
  /** patient_illness.id — null แปลว่า record ยังไม่ถูกสร้าง (V3 ใช้ !id ตัดสิน new vs update: CardPatientInfo.tsx:292) */
  illnessId: number | null;
  /** patient_history.id — null เมื่อ backend ไม่ส่ง id มา (V3 save history แยก endpoint) */
  historyId: number | null;
  chiefComplaint: string;
  presentIllness: string;
  pastIllness: string;
  personalHistory: string;
  familyHistory: string;
  sensitiveNote: string;
  advice: string;
};

export const SubjectiveInitial: Subjective = {
  illnessId: null,
  historyId: null,
  chiefComplaint: "",
  presentIllness: "",
  pastIllness: "",
  personalHistory: "",
  familyHistory: "",
  sensitiveNote: "",
  advice: "",
};

export function subjectiveFromEmr(emrPayload: unknown): Subjective {
  const emr = asDict(emrPayload);
  const illness = asDict(emr.patient_illness);
  const history = asDict(emr.patient_history);
  const sensitive = asDict(emr.sensitive_note);

  return {
    illnessId: asNumber(illness.id) ?? null,
    historyId: asNumber(history.id) ?? null,
    /* 86624 ข้อ 1: "อาการนำ (Chief complaint)" คือช่องของ chief_complaint
       ถ้าแพทย์ยังไม่เคยบันทึก ให้เอาของ encounter (จากการคัดกรอง) มาเป็นค่าเริ่มต้น
       ห้ามเอา encounter_chief_complaint ไปยัดช่อง Present illness แบบโค้ดเดิม */
    chiefComplaint: asText(illness.chief_complaint) || asText(emr.encounter_chief_complaint),
    presentIllness: asText(illness.present_illness),
    pastIllness: asText(history.past_illness),
    personalHistory: asText(history.personal_history),
    familyHistory: asText(history.family_history),
    sensitiveNote: asText(sensitive.sensitive_note),
    advice: asText(illness.advice),
  };
}

/** chief complaint จากการคัดกรอง (TriageFromEncounterSerializer_core) */
export type TriageInfo = {
  triageId: number | null;
  chiefComplaint: string;
  triageLevel: string;
  arriveStatus: string;
};

export const TriageInitial: TriageInfo = { triageId: null, chiefComplaint: "", triageLevel: "", arriveStatus: "" };

export function triageFromApi(payload: unknown): TriageInfo {
  const data = asDict(payload);
  return {
    triageId: asNumber(data.triage) ?? null,
    chiefComplaint: decodeHtml(data.chief_complaint),
    triageLevel: asText(data.triage_level),
    arriveStatus: asText(data.arrive_status),
  };
}

/* ============================================================== */
/*   SECTION 4 · OBJECTIVE · ตรวจร่างกาย (organ + template)        */
/* ============================================================== */

/** PhysicalExaminationSerializer_apps_DPO.status */
export type ExamStatus = "X" | "N" | "A" | null;

export type ExamRow = {
  /** key ใน React list: organ:<id> หรือ tpl:<template_item> */
  key: string;
  /** FK organ (API field name คือ "organ") */
  organ: number | null;
  organName: string;
  /** ตัวเลือกจริงจาก master (test_result_option_list) + "N/A" แบบ V3 (CardPhysicalExam.tsx:38-42) */
  options: string[];
  /** "" = ยังไม่ตอบ, "N/A" = ไม่ตรวจ */
  result: string;
  description: string;
  status: ExamStatus;
  /** null = แถว organ ตรง ๆ, number = แถวจาก template */
  templateItem: number | null;
  emr: number | null;
  /** ชื่อ template ของแถว (ว่างเมื่อเป็นแถว organ ตรง ๆ) */
  groupName: string;
  /** id ของ template ที่แถวนี้สังกัด (ใช้กับตัวเลือก Template: เก็บเฉพาะชุดที่เลือก) */
  templateId?: number | null;
};

export type ExamOther = { id: number | null; otherDetail: string; emr: number | null };

export const ExamOtherInitial: ExamOther = { id: null, otherDetail: "", emr: null };

const statusOf = (value: unknown): ExamStatus => {
  const text = asText(value).toUpperCase();
  return text === "X" || text === "N" || text === "A" ? text : null;
};

const rowFromOrganMaster = (organ: Dict): ExamRow => {
  const organId = asNumber(organ.organ_id) ?? asNumber(organ.id) ?? null;
  const options = (Array.isArray(organ.test_result_option_list) ? organ.test_result_option_list : [])
    .map(trimOption)
    .filter((option) => option !== "");

  return {
    key: `organ:${organId ?? asText(organ.organ_name)}`,
    organ: organId,
    organName: asText(organ.organ_name) || asText(organ.name),
    options: options.length ? [...options, "N/A"] : ["N/A"],
    result: "",
    description: "",
    status: null,
    templateItem: null,
    emr: asNumber(organ.emr) ?? null,
    groupName: "",
  };
};

const savedRowKey = (row: Dict): string => {
  const templateItem = asNumber(row.template_item);
  if (templateItem !== undefined) return `tpl:${templateItem}`;
  return `organ:${asNumber(row.organ) ?? asNumber(row.organ_id) ?? ""}`;
};

const applySaved = (row: ExamRow, saved: Dict): ExamRow => ({
  ...row,
  organName: row.organName || asText(saved.organ_name),
  organ: row.organ ?? asNumber(saved.organ) ?? asNumber(saved.organ_id) ?? null,
  result: asText(saved.result),
  description: decodeHtml(saved.description),
  status: statusOf(saved.status),
  emr: asNumber(saved.emr) ?? row.emr ?? null,
});

/**
 * master organ + แถวที่เก็บไว้ -> แถวสำหรับ render
 * แถวที่เก็บไว้แต่ไม่มีใน master จะถูกต่อท้ายข้อมูลไม่หาย (organ master เปลี่ยนตาม division)
 */
/**
 * payload ทั้งชุดของ LoadExam -> values ที่ card ใช้ render
 * แยกมาเป็น pure function เพราะไฟล์ sequence import issara-sdk ซึ่ง test ใน jsdom/jest ลากทั้งชุดไม่ได้
 * และเพราะบั๊กที่เคยเกิดคือ "payload เป็น { items: [...] } แต่ mapper อ่าน array ตรง ๆ" -> แถวหายทั้งชุด
 */
/* ============================================================== */
/*   ประวัติแพ้ (ADR) + โรคประจำตัว (DPO problem list)             */
/* ============================================================== */

/** แถว ADR ตาม AdverseReactionSerializer (V3 กรอง status !== "NO_REACTION" ใน PatientPanel) */
export type AllergyItem = {
  id: number | null;
  name: string;
  /** อาการ/หมายเหตุ เช่น "ลมพิษ + หายใจลำบาก" */
  note: string;
  status: string;
  edited: string;
  edit_user?: string;
};

/** แถว problem list ตาม ProblemListSerializer_apps_DPO (id, message, is_active, edit_user) */
export type ProblemItem = {
  id: number | null;
  message: string;
  edited: string;
  /** เก็บไว้ให้ PatientPanelV4 กรองต่อได้ (แถวนี้ผ่าน is_active มาแล้ว) */
  is_active: true;
};

/** field ของ ADR ที่ HIS V4 ใช้ (V3 CardPatientInfo.tsx:214-220 ส่ง params ชุดนี้) */
export const allergyListParams = (patientId?: number): Dict => ({
  ...(patientId === undefined ? {} : { patient: patientId }),
  exclude_unused: true,
  severe_first: true,
});

export const problemListParams = (patientId?: number): Dict => ({
  ...(patientId === undefined ? {} : { patient: patientId }),
  is_active: true,
});

export function allergyItemsFromApi(payload: unknown): AllergyItem[] {
  return itemsOf(payload)
    .map((row) => ({
      id: asNumber(row.id) ?? null,
      name: asText(row.name),
      note: asText(row.note),
      status: asText(row.status),
      edited: asText(row.edited),
      edit_user: asText(row.edit_user),
    }))
    /* "ไม่แพ้" ที่บันทึกไว้จริงไม่ใช่ประวัติแพ้ -> panel แสดง "ไม่มีประวัติแพ้" (V3 กรองแบบเดียวกัน) */
    .filter((item) => item.status !== "NO_REACTION" && item.name.trim() !== "");
}

export function problemItemsFromApi(payload: unknown): ProblemItem[] {
  return itemsOf(payload)
    /*ProblemListSerializer มี is_active ให้กรองโรคที่ปิดไปแล้วออก (V4 ใช้ flag "โรคประจำตัว") */
    .filter((row) => row.is_active === undefined || row.is_active !== false)
    .map((row) => ({
      id: asNumber(row.id) ?? null,
      message: asText(row.message ?? row.description),
      edited: asText(row.edited),
      is_active: true as const,
    }))
    .filter((item) => item.message.trim() !== "");
}

/**
 * contract ของประวัติแพ้ (verify จาก staging API + OPTIONS /apis/ADR/adverse-reaction/)
 *   POST  body {patient, action, note}                  -> 201 (status "NOTED")
 *   PATCH body {action: "PAUSE" | "CANCEL", note}       -> 200 (status "PAUSED" / "CANCELED")
 *     และ CANCELซ้ำบนแถวที่ CANCELED แล้ว -> 400 ACTION_NOT_ALLOWED
 *   PATCH ที่ส่งเฉพาะ note หรือเฉพาะ is_inactive (ไม่มี action) -> 500 KeyError
 *     -> V4 ต้องส่ง action+note คคู่กันทุก PATCH
 *   PATCH body {action:"CANCEL", ..., is_inactive:true}   -> 400 (is_inactive ปิดการแสดงผล ไม่ใช่ตัวลบ record)
 *   DELETE /apis/ADR/adverse-reaction/{pk}/             -> 405 (ไม่รองรับ -> ปิดใช้งานด้วย action CANCEL)
 * field `name`/`status`/`adr_short_info` เป็น read-only (backend derive จาก items) ->
 *   record ที่ไม่มี items จึงมี name = "" และ V4 ต้องแสดง noteแทน
 */
export const ADR_ACTION_SAVE = "NOTE";
export const ADR_ACTION_PAUSE = "PAUSE";
export const ADR_ACTION_CANCEL = "CANCEL";

export function buildAdrPayload(args: {
  patientId?: number;
  note: string;
  action?: string;
  adversaryType?: number;
  otherSymptom?: string;
}): Dict {
  const payload: Dict = {
    action: args.action ?? ADR_ACTION_SAVE,
    note: `${args.note ?? ""}`.trim(),
  };
  if (args.patientId !== undefined) payload.patient = args.patientId;
  if (args.adversaryType !== undefined) payload.type = args.adversaryType;
  if (args.otherSymptom !== undefined) payload.other_symptom = args.otherSymptom;
  return payload;
}

/**
 * contract ของโรคประจำตัว (ProblemListSerializer_apps_DPO)
 *   POST  /apis/DPO/problem-list/ body {patient, message}  -> 201
 *   PATCH /apis/DPO/problem-list/{pk}/ body {message}        -> 200 (PATCH partial ได้)
 *   PATCH ... body {"is_active": false}                      -> 200 (list ?is_active=true ไม่คืนรายการอีก)
 *   PUT   body {message} เท่านั้น                             -> 400 {"patient":["ระบุข้อมูลในช่องนี้"]}
 *   DELETE                                              -> 405 (ไม่รองรับ)
 */
export function buildProblemPayload(args: {
  patientId?: number;
  message?: string;
  active?: boolean;
}): Dict {
  const payload: Dict = {};
  if (args.message !== undefined) payload.message = `${args.message}`.trim();
  if (args.active !== undefined) payload.is_active = args.active;
  if (args.patientId !== undefined) payload.patient = args.patientId;
  return payload;
}

export type ExamLoadPayloads = {
  emr: unknown;
  organMaster: unknown;
  templates: unknown;
  diagnosis: unknown;
  triage: unknown;
  vitals: unknown;
  /** AdverseReactionList_apps_ADR (ประวัติแพ้) */
  allergy?: unknown;
  /** ProblemListList_apps_DPO (โรคประจำตัว) */
  problems?: unknown;
};

export type ExamLoadResult = {
  subjective: Subjective;
  examRows: ExamRow[];
  templateRows: ExamRow[];
  other: ExamOther;
  diagnosis: DiagnosisState;
  /** flag การอนุมัติวินิจฉัย (need_approve/student_user/approved_by) บน response ของ diagnosis/ */
  diagnosisApproval: DiagnosisApproval;
  /** record ของ sensitive_note ที่ embedded อยู่ใน payload ของ medical_record/emr/{emr}/ */
  sensitiveNoteRecord: SensitiveNoteRecord;
  /** template ที่ EMR นี้เคยบันทึกไว้ (exam_template) */
  selectedTemplateIds: number[];
  triage: TriageInfo;
  vitals: VitalChip[];
  allergyItems: AllergyItem[];
  problemItems: ProblemItem[];
};

export function examLoadFromApiPayloads(payloads: ExamLoadPayloads): ExamLoadResult {
  const emr = asDict(payloads.emr);
  const savedExam = emr.patient_exam;
  const triage = triageFromApi(payloads.triage);
  const subjective = subjectiveFromEmr(emr);

  return {
    /* 86624 ข้อ 1: อาการนำหน้าตรวจรักษาต้องต่อยอดจากการคัดกรองของ encounter นี้
       (แพทย์แก้ได้; เมื่อแพทย์บันทึกแล้ว ค่าของแพทย์เองเป็นตัวที่ใช้) */
    subjective:
      subjective.chiefComplaint === "" && triage.chiefComplaint !== ""
        ? { ...subjective, chiefComplaint: triage.chiefComplaint }
        : subjective,
    examRows: examRowsFromApi(payloads.organMaster, savedExam),
    templateRows: templateRowsFromApi(payloads.templates, emr.exam_template),
    selectedTemplateIds: selectedTemplateIdsFromApi(emr.exam_template),
    other: otherFromEmr(emr),
    diagnosis: diagnosisFromApi(payloads.diagnosis),
    diagnosisApproval: diagnosisApprovalFromApi(payloads.diagnosis),
    sensitiveNoteRecord: sensitiveNoteRecordFromApi(asDict(payloads.emr).sensitive_note),
    triage: triageFromApi(payloads.triage),
    vitals: vitalsFromApi(payloads.vitals),
    allergyItems: allergyItemsFromApi(payloads.allergy),
    problemItems: problemItemsFromApi(payloads.problems),
  };
}

export function examRowsFromApi(organMaster: unknown, savedExam: unknown): ExamRow[] {
  /* physical_exam_organ/ ตอบมาเป็น { items: [...] } (V3 อ่าน data.items: CardPatientInfo.tsx:342-344)
     ถ้าใช้ asList ตรง ๆ จะ ได้ [] -> grid ตรวจร่างกายหายทั้งชุด เหลือแค่ช่องพิมพ์เอง */
  const rows = itemsOf(organMaster).map(rowFromOrganMaster);
  const saved = asList(savedExam).filter((item) => asNumber(item.template_item) === undefined);
  const byOrganKey = new Map<string, ExamRow>(rows.map((row) => [row.key, row]));

  for (const item of saved) {
    const key = savedRowKey(item);
    const existing = byOrganKey.get(key);
    if (existing) {
      byOrganKey.set(key, applySaved(existing, item));
      continue;
    }
    const appended = applySaved(rowFromOrganMaster({ organ_id: asNumber(item.organ), organ_name: item.organ_name }), item);
    rows.push({ ...appended, key });
    byOrganKey.set(key, appended);
  }

  return Array.from(byOrganKey.values());
}

/**
 * template rows ตาม V3 (CardPhysicalExam.tsx:250-276, sequence/PhysicalExam.ts GET_TEMPLATE)
 * master template: { id, template_name, items: [...] } (PhysicalExamTemplateSerializer_apps_DPO)
 * คำตอบที่เคยบันทึกอยู่ใน emr.exam_template รูปเดียวกับ master แต่ items มี result/description/status/emr
 * (patient_exam ของ BE กรอง template_item=None ออกแล้ว: DPO/views.py PatientEMRList)
 * template ที่เคยบันทึกแต่ไม่อยู่ใน master ของแผนกนี้ คงไว้ ไม่งั้น save จะลบคำตอบเดิมทิ้ง
 */
export function templateRowsFromApi(templates: unknown, savedTemplates: unknown): ExamRow[] {
  const saved = asList(savedTemplates);
  const savedById = new Map<number | undefined, Dict>(saved.map((group) => [asNumber(group.id), group]));
  const master = itemsOf(templates);
  const masterIds = master.map((group) => asNumber(group.id));

  const groups = [
    ...master.map((group) => savedById.get(asNumber(group.id)) ?? group),
    ...saved.filter((group) => !masterIds.includes(asNumber(group.id))),
  ];

  return groups.flatMap((group) =>
    asList(group.items).map((item): ExamRow => {
      const templateItem = asNumber(item.id) ?? null;
      const options = (Array.isArray(item.test_result_option_list) ? item.test_result_option_list : [])
        .map(trimOption)
        .filter((option) => option !== "");

      const base: ExamRow = {
        key: `tpl:${templateItem}`,
        organ: asNumber(item.organ) ?? null,
        organName: asText(item.organ_name),
        options: options.length ? [...options, "N/A"] : ["N/A"],
        result: "",
        description: "",
        status: null,
        templateItem,
        emr: null,
        groupName: asText(group.template_name),
        templateId: asNumber(group.id) ?? null,
      };

      return item.result === undefined ? base : applySaved(base, item);
    }),
  );
}

export const selectedTemplateIdsFromApi = (savedTemplates: unknown): number[] =>
  asList(savedTemplates)
    .map((group) => asNumber(group.id))
    .filter((id): id is number => id !== undefined);

/** V3 ส่งเฉพาะ template ที่เลือก (selectedTemplateList) BE ลบแถวของ template ที่ถูกเอาออกให้เอง */
export const selectedTemplateRows = (rows: ExamRow[], selectedIds: number[]): ExamRow[] =>
  rows.filter((row) => row.templateId !== null && row.templateId !== undefined && selectedIds.includes(row.templateId));

/** แถวที่ "ตอบแล้ว" นับเป็นตรวจร่างกายอย่างน้อย 1 ระบบ (ตรงกับเงื่อนไข design) */
export const isAnsweredRow = (row: ExamRow): boolean => row.result !== "";

export const isUnansweredRow = (row: ExamRow): boolean => row.result === "";

export const isNotApplicableRow = (row: ExamRow): boolean => row.result === "N/A";

/* ============================================================== */
/*   Template การตรวจร่างกาย (issue 86625 ข้อ 4)                   */
/* ============================================================== */

/** master ของแต่ละหน่วยงานเก็บ option ต่างชื่อ (Normal/Abnormal หรือ ปกติ/ผิดปกติ) -> แสดงภาษาไทยตาม design */
export const PE_RESULT_LABELS: Record<string, string> = {
  "": "เลือกผล",
  "-": "เลือกผล",
  Normal: "ปกติ",
  normal: "ปกติ",
  Abnormal: "ผิดปกติ",
  abnormal: "ผิดปกติ",
  "N/A": "ไม่ได้ตรวจ",
  "ไม่ตรวจ": "ไม่ได้ตรวจ",
};

export const peOptionLabelOf = (option: string): string => PE_RESULT_LABELS[option] ?? option;

/** dropdown ของแถวใน template: เลือกผล / ปกติ / ผิดปกติ / ไม่ได้ตรวจ (86625 ข้อ 4) */
export const templateRowOptions = (row: ExamRow): { value: string; text: string }[] => {
  const options = row.options.filter((option) => option !== "" && option !== "N/A");
  const list = options.length > 0 ? options : ["ปกติ", "ผิดปกติ"];
  return [
    { value: "-", text: "เลือกผล" },
    ...list.map((option) => ({ value: option, text: peOptionLabelOf(option) })),
    { value: "N/A", text: "ไม่ได้ตรวจ" },
  ];
};

/** "ปกติ" ทั้งคำไทยและค่าจาก master ภาษาอังกฤษ (ใช้ auto-fill รายละเอียด + นับว่าตอบแล้ว) */
export const isNormalResult = (value: string): boolean =>
  value === "ปกติ" || value.toLowerCase() === "normal";

/** นับแถวของ template ที่กรอกผลแล้ว (ปกติหรือผิดปกติ เท่านั้น, "ไม่ได้ตรวจ" ไม่นับ) */
export const answeredTemplateRows = (rows: ExamRow[], results: Record<string, string> = {}): number =>
  rows.filter((row) => {
    const value = results[row.key] ?? row.result;
    return value !== "" && value !== "N/A";
  }).length;

/**
 * V3 ไม่เคยเซ็ต status เอง (ส่งผ่านค่าจากแถวที่เก็บไว้เท่านั้น: CardPatientInfo.tsx:473-479)
 * จึงตัดสิน "ผิดปกติ" จาก status ก่อน ถ้าไม่มีก็ดู text
 * TODO(backend): ยืนยัน semantic ของ X / N / A
 */
const ABNORMAL_TEXT = /(abnormal|ผิดปกติ|ผิดปกติ|abnormality)/i;
const NORMAL_TEXT = /(normal|ปกติ)/i;

export function isAbnormalRow(row: ExamRow): boolean {
  if (row.status === "A") return true;
  if (row.status === "N" || row.status === "X") return false;
  if (row.result === "" || row.result === "N/A") return false;
  if (ABNORMAL_TEXT.test(row.result)) return true;
  if (NORMAL_TEXT.test(row.result)) return false;
  return false;
}

export function otherFromEmr(emrPayload: unknown): ExamOther {
  const other = asDict(asDict(emrPayload).patient_exam_other);
  return {
    id: asNumber(other.id) ?? null,
    otherDetail: decodeHtml(other.other_detail),
    emr: asNumber(other.emr) ?? null,
  };
}

/* ============================================================== */
/*   SECTION 5 · payload เขียนหน้าตรวจรักษา (patient_screen)        */
/* ============================================================== */

/**
 * copy กฎ normalization จาก V3 (CardPatientInfo.tsx:473-506):
 * result ""   -> "N/A" และลบ description
 * result "N/A" -> ลบ description
 */
export function toExamPayloadRow(row: ExamRow, emrId?: number): Dict {
  const result = row.result === "" ? "N/A" : row.result;
  const description = result === "N/A" ? "" : row.description;

  const payload: Dict = {
    organ: row.organ,
    status: row.status,
    result,
    description,
    /* BE ไม่ส่ง emr ใน master/patient_exam แต่ serializer ต้องมี (V3: CardPhysicalExam ตั้ง emr ทุกแถว) */
    emr: emrId ?? row.emr,
  };
  if (row.templateItem !== null) payload.template_item = row.templateItem;
  return payload;
}

export type PatientScreenPayload = {
  patient_illness: Dict;
  patient_exam: Dict[];
  patient_exam_other: Dict;
};

export function buildPatientScreenPayload(args: {
  ids: ExamIds;
  subjective: Subjective;
  rows: ExamRow[];
  other: ExamOther;
}): PatientScreenPayload {
  const { ids, subjective, rows, other } = args;

  const patient_illness: Dict = {
    chief_complaint: subjective.chiefComplaint,
    present_illness: subjective.presentIllness,
    advice: subjective.advice,
    emr: ids.emrId,
  };
  if (subjective.illnessId !== null) patient_illness.id = subjective.illnessId;

  const patient_exam_other: Dict = { other_detail: other.otherDetail, emr: ids.emrId };
  if (other.id !== null) patient_exam_other.id = other.id;

  return {
    patient_illness,
    patient_exam: rows.map((row) => toExamPayloadRow(row, ids.emrId)),
    patient_exam_other,
  };
}

/*
 * ไม่ส่ง doctor ใน patient_illness/patient_exam_other (เหมือน V3 CardPatientInfo.setSaveData)
 * POST: PatientScreenSerializer.create ส่ง doctor= ให้ objects.create เอง ถ้า payload มีด้วยจะ TypeError (500)
 * ส่ง doctor เฉพาะตอนเรียกเข้าตรวจ (buildExamStartPayload) ซึ่งเป็น PUT
 */

/**
 * ประวัติ past/personal/family save แยกจาก patient_screen
 * PUT /apis/DPO/patient_history/emr/{emr}/ (DPOI.ts:788-796)
 */
export function buildPatientHistoryPayload(args: { emrId: number; subjective: Subjective }): Dict {
  return {
    emr: args.emrId,
    past_illness: args.subjective.pastIllness,
    personal_history: args.subjective.personalHistory,
    family_history: args.subjective.familyHistory,
  };
}

/**
 * record ของ sensitive note (backend: ClassifiedPatientHistorySerializer_apps_DPO)
 * endpoint `GET /apis/DPO/sensitive_note/emr/{emr}/` คืน `{id, edit_user, created, edited, sensitive_note, emr, doctor, division:[id]}`
 * ส่วน payload ของ `GET /apis/DPO/medical_record/emr/{emr}/` คืนเป็น sub-object `{id, sensitive_note, division:[{id, code, name}]}`
 * และ **field นี้มีมาด้วยเฉพาะตอน send header `division` เท่านั้น** (verify จาก staging แล้ว: ไม่มี header -> ไม่มี key)
 */
export type SensitiveNoteRecord = {
  /** id ของ record (V3 ใช้ deciding new vs old: SubPatientHistory.tsx:185) */
  id: number | undefined;
  note: string;
  editUser: string;
  editedAt: string;
  divisionIds: number[];
};

export const SensitiveNoteRecordInitial: SensitiveNoteRecord = {
  id: undefined,
  note: "",
  editUser: "",
  editedAt: "",
  divisionIds: [],
};

export function sensitiveNoteRecordFromApi(payload: unknown): SensitiveNoteRecord {
  const data = asDict(payload);
  if (data.id === undefined && data.sensitive_note === undefined) return SensitiveNoteRecordInitial;

  const divisionIds = (Array.isArray(data.division) ? (data.division as unknown[]) : [])
    .map((item) => (typeof item === "object" && item !== null ? asNumber((item as Dict).id) : asNumber(item)))
    .filter((id): id is number => id !== undefined);

  return {
    id: asNumber(data.id),
    note: decodeHtml(data.sensitive_note),
    editUser: decodeHtml(data.edit_user),
    editedAt: asText(data.edited),
    divisionIds,
  };
}

/**
 * V3 split เป็น 2 case (SubPatientHistory.tsx:185-210):
 *   record มี id อยู่แล้ว  -> PUT /apis/DPO/sensitive_note/emr/{emr}/ ทั้ง record
 *   record ยังไม่มี           -> POST (backend สร้างให้)
 * V4 ใช้ PATCH เมื่อ record มีอยู่ (ส่งเฉพาะ sensitive_note) เพราะ PUT validate ทั้ง record
 * และต้องมี division ไม่งั้น backend ตอบ 400 {"division":["ระบุข้อมูลในช่องนี้"]}
 */
export function buildSensitiveNoteUpdatePayload(args: { note: string }): Dict {
  return { sensitive_note: args.note };
}

export function buildSensitiveNoteCreatePayload(args: {
  emrId: number;
  divisionId: number;
  note: string;
}): Dict {
  return {
    emr: args.emrId,
    division: [args.divisionId],
    sensitive_note: args.note,
  };
}

/** V3 ทาสีปุ่ม History เมื่อประวัติหรือ sensitive note มีเนื้อหา (CardPatientInfo.tsx:326-335) */
export const hasHistoryContent = (
  texts: {
    pastIllness: string;
    personalHistory: string;
    familyHistory: string;
    sensitiveNote: string;
  },
  record: SensitiveNoteRecord,
): boolean =>
  [
    texts.pastIllness,
    texts.personalHistory,
    texts.familyHistory,
    texts.sensitiveNote,
    record.note,
  ].some((text) => `${text ?? ""}`.trim() !== "");

export const hasHistoryValues = (subjective: Subjective): boolean =>
  subjective.pastIllness.trim() !== "" ||
  subjective.personalHistory.trim() !== "" ||
  subjective.familyHistory.trim() !== "";

/** payload สำหรับ seeding เมื่อเรียกผู้ป่วยเข้าตรวจ (V3 IN_QUEUE -> IN_EXAM: CardPatientListInterface.ts:1904-1912) */
export function buildExamStartPayload(ids: ExamIds): PatientScreenPayload {
  return {
    patient_illness: { emr: ids.emrId, doctor: ids.doctorId ?? null },
    patient_exam: [],
    patient_exam_other: { emr: ids.emrId, doctor: ids.doctorId ?? null },
  };
}

/* ============================================================== */
/*   SECTION 6 · DIAGNOSIS (ICD-10)                               */
/* ============================================================== */

/** DIAGNOSIS_TYPE_PRIMARY / SECONDARY (CardICD10.tsx:867-868) */
export const DIAGNOSIS_TYPE_PRIMARY = 1;
export const DIAGNOSIS_TYPE_SECONDARY = 2;

/** แถววินิจฉัยที่ใช้ใน UI และใช้เป็น buffer ก่อน save */
export type DiagnosisRow = {
  pk: number | null;
  emr: number | null;
  type: number;
  icd10: number | null;
  icd10MedTerm: number | null;
  /** จาก API row (display) principal/secondary_diagnosis[].icd_code / icd_term */
  icdCode: string;
  icdTerm: string;
  medicalDescription: string;
  detail: string;
  activeDisease: boolean;
};

export const DiagnosisRowInitial: DiagnosisRow = {
  pk: null,
  emr: null,
  type: DIAGNOSIS_TYPE_PRIMARY,
  icd10: null,
  icd10MedTerm: null,
  icdCode: "",
  icdTerm: "",
  medicalDescription: "",
  detail: "",
  activeDisease: false,
};

export type DiagnosisState = { principal: DiagnosisRow | null; secondary: DiagnosisRow[] };

export const DiagnosisInitial: DiagnosisState = { principal: null, secondary: [] };

const diagnosisRowFromApi = (row: Dict, type: number): DiagnosisRow => ({
  pk: asNumber(row.pk) ?? asNumber(row.id) ?? null,
  emr: asNumber(row.emr) ?? null,
  type: asNumber(row.type) ?? type,
  icd10: asNumber(row.icd10) ?? null,
  icd10MedTerm: asNumber(row.icd10_med_term) ?? null,
  icdCode: asText(row.icd_code),
  icdTerm: decodeHtml(row.icd_term),
  medicalDescription: decodeHtml(row.medical_description),
  detail: decodeHtml(row.detail),
  activeDisease: asBoolean(row.active_disease),
});

/** GET/PUT /apis/DPO/medical_record/{emr}/diagnosis/ */
export function diagnosisFromApi(payload: unknown): DiagnosisState {
  const data = asDict(payload);
  const principal = asList(data.principal_diagnosis)[0];
  return {
    principal: principal ? diagnosisRowFromApi(principal, DIAGNOSIS_TYPE_PRIMARY) : null,
    secondary: asList(data.secondary_diagnosis).map((row) =>
      diagnosisRowFromApi(row, DIAGNOSIS_TYPE_SECONDARY),
    ),
  };
}

/** แถวผลการค้นหาจาก POST /apis/core/icd10KeyUp/{method} (ImagingResult.ts:345-366) */
/** method ของ /apis/core/icd10KeyUp/{method} (V3 CardICD10.tsx) */
export type IcdSearchMethod = "medterm" | "icdcode" | "icdterm";

export type IcdHit = {
  icd10Id: number | null;
  code: string;
  term: string;
  medTerm: string;
  hasSublevel: boolean;
  /**
   * Icd10MedicalTerm.id ของแถวที่ค้นหาเจอ (backend icd10KeyUp ตอบ medterm_id เฉพาะ method=medterm)
   * ใช้เชื่อม Diagnosis.icd10_med_term (core/models.py:9207) — V3 ทำแบบเดียวกัน
   * (HISV3/DPO/sequence/TreatmentResult.ts:183 `icd9cm_med_term: item.medterm_id`)
   * method icdcode / icdterm / icd10KeyUp base.py:4738-4742 ไม่ตอบ medterm_id -> เป็น null
   */
  medtermId?: number | null;
  /** key ประจำแถวผลค้นหา (สร้างโดย icdHitKey) - ดู icdHitKey ด้านล่าง */
  key?: string;
};

/**
 * 1 รหัส ICD-10 มี medical term ได้หลายแถว เช่น X503 ->
 *   "ปวดเข่าขณะเล่นตะกร้อ" (medterm_id 27330), "ปวดเข่าขณะเล่นฟุตบอล" (27331),
 *   "วิ่งออกกำลังกายแล้วปวดเข่า" (27332)
 * ถ้าจับแถวกลับด้วย code อย่างเดียวจะได้แถวแรกเสมอ -> ช่อง Medical Term เติมคำผิด
 * จึงใช้ key ที่ต่างกันต่อแถว (method=medterm ใช้ medterm_id, method อื่นใช้ code+term)
 */
export type IcdKeySource = { code?: string; term?: string; medTerm?: string; medtermId?: number | null };

export const icdHitKey = (hit: IcdKeySource): string => {
  const medtermId = hit.medtermId === undefined ? null : hit.medtermId;
  if (medtermId !== null) return `m${medtermId}`;
  const text = String(hit.medTerm ?? "").trim() || String(hit.term ?? "").trim();
  return `c${String(hit.code ?? "").trim()}|${text}`;
};

/** ผลค้นหา ICD-10 ที่แสดงบนหน้าเดียว (issue 86627 ข้อ 1.1 "แสดงผลสูงสุด 6 รายการ") */
export const ICD_RESULT_LIMIT = 6;

/**
 * ผลค้นหาที่ใช้แสดง (86627):
 * - ตัดรหัสที่เลือกไปแล้ว (Principal + Secondary) ออกจากผลค้นหา
 * - จำกัด 6 รายการ (API icd10KeyUp ตอบได้สูงสุด 500 แถว)
 */
export function icdHitsForSearch(hits: IcdHit[], usedCodes: string[]): IcdHit[] {
  const used = new Set(usedCodes.map((code) => String(code).trim().toLowerCase()));
  return hits
    .filter((hit) => !used.has(String(hit.code).trim().toLowerCase()))
    .slice(0, ICD_RESULT_LIMIT);
}

/** รหัสซ้ำกับ Principal หรือ Secondary ที่มีอยู่ เลือกไม่ได้ (86627 ข้อ 2.3) */
export const isIcdCodeUsed = (code: string, usedCodes: string[]): boolean =>
  usedCodes.some((used) => String(used).trim().toLowerCase() === String(code ?? "").trim().toLowerCase());

export function icdHitsFromSearch(payload: unknown): IcdHit[] {
  const rows = asList(asDict(payload).response).length
    ? asList(asDict(payload).response)
    : itemsOf(payload);

  return rows.map((row) => ({
    icd10Id: asNumber(row.icd10_id) ?? asNumber(row.id) ?? null,
    code: asText(row.icdcode ?? row.code),
    term: decodeHtml(row.icdterm ?? row.term ?? row.icd_term),
    medTerm: decodeHtml(row.medterm ?? row.medical_description),
    hasSublevel: asBoolean(row.has_sublevel),
    medtermId: asNumber(row.medterm_id) ?? null,
  })).map((hit) => ({ ...hit, key: icdHitKey(hit) }));
}

/**
 * flag การอนุมัติวินิจฉัยบน GET/PUT /apis/DPO/medical_record/{emr}/diagnosis/
 * (verify จาก staging แล้ว: response มี need_approve, student, student_name, student_user,
 *  approved_by, approved_by_name, approved_at)
 *
 * V3: ผู้ใช้ที่เป็นนักศึกษา (state.isStudentUser = selectedEncounter.student_user === user.id) ต้องผ่าน
 * modal ModAuthen "ยืนยันการตรวจสอบ" และส่ง approved_by_username/approved_by_password ตอน save
 * (apps/DPO/CardICD10.tsx:428-436, 846-858; HISV3/DPO/CardPatientInfo.tsx:886-899)
 */
export type DiagnosisApproval = {
  needApprove: boolean;
  student: number | null;
  studentUser: number | null;
  studentName: string;
  approvedBy: number | null;
  approvedByName: string;
  approvedAt: string;
};

export const DiagnosisApprovalInitial: DiagnosisApproval = {
  needApprove: false,
  student: null,
  studentUser: null,
  studentName: "",
  approvedBy: null,
  approvedByName: "",
  approvedAt: "",
};

export function diagnosisApprovalFromApi(payload: unknown): DiagnosisApproval {
  const data = asDict(payload);
  return {
    needApprove: asBoolean(data.need_approve),
    student: asNumber(data.student) ?? null,
    studentUser: asNumber(data.student_user) ?? null,
    studentName: decodeHtml(data.student_name),
    approvedBy: asNumber(data.approved_by) ?? null,
    approvedByName: decodeHtml(data.approved_by_name),
    approvedAt: asText(data.approved_at),
  };
}

/**
 * ต้องให้ supervisor (แพทย์ผู้ดูแล) ยืนยันก่อน save
 * V3 gate ด้วย state.isStudentUser = selectedEncounter.student_user === django.user.id
 * (HISV3/common/CardPatientListInterface.ts:800, apps/DPO/CardICD10.tsx:428-436)
 *
 * V4 ใช้ 2 เงื่อนไขรวมกัน (อย่างใดอย่างหนึ่ง):
 *   1) user ที่ login อยู่เป็นคนเดียวกับ student_user บน record -> นักศึกษาต้องให้ supervisor พิมพ์ credential
 *   2) backend แจ้งว่าต้องมีการอนุมัติ (need_approve) แต่ยังไม่มีใครอนุมัติ (approved_by ว่าง)
 */
export const needsSupervisorApproval = (
  approval: DiagnosisApproval,
  currentUserId?: number,
): boolean => {
  /* อนุมัติมาแล้ว (approved_by มีค่า) -> ไม่ต้องขออีก */
  if (approval.approvedBy !== null) return false;

  const isStudent =
    approval.studentUser !== null && currentUserId !== undefined && approval.studentUser === currentUserId;
  const pendingApprove = approval.needApprove;
  return isStudent || pendingApprove;
};

/**
 * สร้าง payload save diagnosis (DPOI.ts:181-192)
 * V3 ลบ pk ทิ้งเมื่อเป็น null ก่อนส่ง (CardICD10.tsx:395-398)
 */
export function toDiagnosisPayloadRow(row: DiagnosisRow, emrId?: number): Dict {
  const payload: Dict = {
    emr: row.emr ?? emrId ?? null,
    type: row.type,
    medical_description: row.medicalDescription,
    icd10: row.icd10,
    icd10_med_term: row.icd10MedTerm,
    detail: row.detail,
    active_disease: row.activeDisease,
  };
  if (row.pk !== null) payload.pk = row.pk;
  return payload;
}

export function buildDiagnosisPayload(
  state: DiagnosisState,
  emrId?: number,
  approval?: { username?: string; password?: string },
): Dict {
  const payload: Dict = {
    principal_diagnosis: state.principal ? [toDiagnosisPayloadRow(state.principal, emrId)] : [],
    secondary_diagnosis: state.secondary.map((row) => toDiagnosisPayloadRow(row, emrId)),
  };
  /* V3: CardICD10.tsx:388-394 ส่ง approved_by_* ทุกครั้ง (backend ignore เมื่อไม่ต้องอนุมัติ)
     ส่งเฉพาะเมื่อมีค่า ไม่ใช่ key ว่าง (DRF reject ค่าว่างบาง field) */
  const username = `${approval?.username ?? ""}`.trim();
  const password = `${approval?.password ?? ""}`;
  if (username !== "") payload.approved_by_username = username;
  if (password !== "") payload.approved_by_password = password;
  return payload;
}

/** เทียบเนื้อหาวินิจฉัยโดยไม่สน pk: แถวใหม่ที่เพิ่งบันทึกจะได้ pk จาก server แต่เนื้อหาเดิม */
export const diagnosisSignature = (state: DiagnosisState): string => {
  const rowOf = (row: DiagnosisRow): unknown[] => [
    row.icd10,
    row.medicalDescription,
    row.detail,
    row.activeDisease,
  ];
  return JSON.stringify([state.principal ? rowOf(state.principal) : null, state.secondary.map(rowOf)]);
};

/** แถววินิจฉัย -> ข้อความที่ใช้แสดงใน UI V4 (design แสดง "[code] term") */
export const diagnosisLabel = (row: DiagnosisRow): string => {
  const code = row.icdCode || "";
  const term = row.medicalDescription || row.icdTerm || "";
  return code ? `[${code}] ${term}` : term;
};

/**
 * แถววินิจฉัยจากผลการค้นหา (CardICD10.tsx:174-186, 268-280)
 * medical_description = hit.medTerm, icd10 = hit.icd10_id
 */
export function diagnosisFromIcdHit(
  hit: IcdHit,
  args: { emrId?: number; type?: number; base?: DiagnosisRow },
): DiagnosisRow {
  const base = args.base ?? DiagnosisRowInitial;
  const sameIcd = hit.icd10Id !== null && hit.icd10Id === base.icd10;
  return {
    ...base,
    emr: args.emrId ?? base.emr ?? null,
    type: args.type ?? base.type,
    icd10: hit.icd10Id,
    medicalDescription: hit.medTerm || hit.term,
    icdCode: hit.code,
    icdTerm: hit.term,
    /* เชื่อม medical term เมื่อผลการค้นหาให้ medterm_id มาด้วย
       ค้นหาด้วยรหัส (method=icdcode) backend ไม่ตอบ medterm_id:
       ถ้ารหัสเดิมอยู่แถวเดิม = คงค่า link เดิมไว้ ไม่ลบทิ้ง
       ถ้าเปลี่ยนรหัส = ล้างเป็น null เพราะ medical term ของอันเดิมไม่เกี่ยวกับอันใหม่ */
    icd10MedTerm: hit.medtermId ?? (sameIcd ? base.icd10MedTerm ?? null : null),
  };
}

/* ============================================================== */
/*   SECTION 6a · รวมค่าที่ยังไม่บันทึกกับข้อมูลจาก server            */
/* ============================================================== */

/**
 * ค่าบนฟอร์มเทียบกับค่าที่ server ส่งมารอบก่อน (baseline)
 *   ไม่ได้แก้ -> ใช้ค่าใหม่ของ server
 *   แก้ค้างอยู่ -> คงค่าที่ผู้ใช้พิมพ์ ไม่ให้ reload/การบันทึกส่วนอื่นทับ
 * บันทึกสำเร็จแล้วค่า server จะตรงกับฟอร์ม จึงกลับเป็น "ไม่ได้แก้" เอง
 * บันทึกล้มเหลว server ยังเป็นค่าเดิม ฟอร์มจึงคงข้อความไว้ให้แก้แล้วบันทึกใหม่
 */
export const keepUnsaved = <T,>(form: T, baseline: T, server: T): T =>
  form === baseline ? server : form;

export const mergeUnsavedMap = (
  form: Record<string, string>,
  baseline: Record<string, string>,
  server: Record<string, string>,
): Record<string, string> => {
  const merged = { ...form };
  for (const key of Object.keys(server)) {
    merged[key] = keepUnsaved(form[key] ?? "", baseline[key] ?? "", server[key]);
  }
  return merged;
};

/* ============================================================== */
/*   SECTION 7 · GALLERY (ภาพการตรวจ)                              */
/* ============================================================== */

/** ClinicalPictureSerializer_apps_DPO */
/**
 * ชั้นเส้นที่วาดบนภาพ (86630 / backend Redmine #86780)
 * DPO.ClinicalPictureAnnotation เก็บ points/position เป็นค่าสัมพัทธ์ 0..1
 * เทียบกับขนาดภาพที่แสดง จึง redraw ได้ทุก scale โดยไม่ทับไฟล์ภาพต้นฉบับ
 */
export type GalleryAnnotationApi = {
  id: number;
  picture: number;
  type: "PEN" | "TEXT";
  color: string;
  width: number;
  /** จุดของเส้นแบบ 0..1 */
  points: Point[];
  text: string;
  position: { x: number; y: number } | null;
  displaySeq: number;
  active: boolean;
  editUser: string;
};

export const GALLERY_ANNOTATION_PEN = "PEN";

export type GalleryApiPhoto = {
  id: number;
  emr: number | null;
  doctor: number | null;
  image: string;
  description: string;
  created: string;
  active: boolean;
  annotations: GalleryAnnotationApi[];
};

export function galleryFromApi(payload: unknown): GalleryApiPhoto[] {
  return itemsOf(payload).map((row) => ({
    id: asNumber(row.id) ?? 0,
    emr: asNumber(row.emr) ?? null,
    doctor: asNumber(row.doctor) ?? null,
    image: asText(row.image),
    description: decodeHtml(row.description),
    created: asText(row.created),
    annotations: galleryAnnotationsFromApi(row.annotations),
    /* ClinicalPictureSerializer ใช้ is_active; บาง view ตอบ active มาให้ */
    active:
      row.is_active === undefined
        ? row.active === undefined
          ? true
          : asBoolean(row.active)
        : asBoolean(row.is_active),
  }));
}

/**
 * POST /apis/DPO/medical_record/gallery/{emr}/
 * body ที่ backend ต้องรับจริง (verify จาก staging): emr + image + description
 * `items` ที่ V3 ส่ง (CardImageGallery.tsx:373-387) API ไม่ได้ใช้ จึงไม่ส่งใน V4
 * image ต้องเป็น data URI ที่ base64 decode ได้และเป็นรูปจริง (png/jpeg)
 */
export function buildGalleryUploadPayload(args: {
  emrId: number;
  image: string;
  description?: string;
}): { emr: number; image: string; description: string } {
  return {
    emr: args.emrId,
    image: args.image,
    description: args.description ?? "",
  };
}

/**
 * PATCH /apis/DPO/medical_record/picture/{pk}/
 * ส่งเฉพาะ field ที่แก้จริง (V3 ใช้ PUT ทั้ง record: src/HIS/Interface/DPOI.ts:510-524)
 * PUT ใน endpointนี้บังคับให้ image เป็นไฟล์เสมอ -> 400 {"image":["ไม่ได้รับไฟล์"]}
 * ถ้าแก้แค่ description/is_active จึงต้อง PATCH เท่านั้น
 */
export function buildPictureUpdatePayload(args: {
  emrId?: number;
  image?: string;
  description?: string;
  active?: boolean;
}): Dict {
  const payload: Dict = {};
  if (args.emrId !== undefined) payload.emr = args.emrId;
  if (args.image !== undefined) payload.image = args.image;
  if (args.description !== undefined) payload.description = args.description;
  if (args.active !== undefined) payload.is_active = args.active;
  return payload;
}

/**
 * รูปแบบภาพ (template) จาก GET /apis/DPO/template-gallery/
 * V3: getTemplateGalleryImage (src/HIS/Interface/DPOI.ts:526-531) -> ModalTemplateFile เลือกหลายรูปแล้ว POST gallery/{emr}/
 * field `image` เป็น path/URL ของไฟล์ (backend ไม่ใช่คนสร้าง record) -> V4 ต้องแปลงเป็น raster data URI ก่อน POST
 */
export type GalleryTemplateItem = {
  id: number;
  name: string;
  image: string;
};

export function galleryTemplatesFromApi(payload: unknown): GalleryTemplateItem[] {
  return itemsOf(payload)
    .filter((row) => row.active !== false)
    .map((row) => ({
      id: asNumber(row.id) ?? 0,
      name: asText(row.name),
      image: asText(row.image),
    }))
    .filter((row) => row.id !== 0 && row.image !== "");
}

/**
 * view ของภาพสำหรับ UI (mock GalleryPhoto + รูปจริงจาก API)
 * API มีแค่ image/description ไม่มี "ประเภทภาพ" -> derive label จาก description
 * TODO(backend/design): ถ้าต้องเก็บประเภทภาพ ต้องเพิ่ม field ฝั่ง API
 */
export type GalleryPhotoView = {
  id: number;
  label: string;
  caption: string;
  at: string;
  by: string;
  image: string;
  strokes: Stroke[];
};

export type Stroke = { color: string; width: number; points: Point[] };

export type Point = [number, number];

/**
 * ClinicalPicture ไม่มี field "ประเภทภาพ" (มีแค่ image + description: DPO/models.py:407-412)
 * V4 จึงเก็บประเภทภาพไว้ใน description ในรูปแบบ "{ประเภท}: {คำอธิบาย}"
 * (แถวเก่าของ V3 ที่เป็นข้อความล้วน ไม่มี prefix -> galleryTypeOf fallback ไป keyword เดิม)
 */
export const GALLERY_DESCRIPTION_SEPARATOR = ": ";

/** สิ่งที่ส่งเข้า API เมื่อผู้ใช้ไม่พิมพ์คำอธิบาย = เก็บเฉพาะชื่อประเภท (86629 ข้อ 3.2 ไม่ควรแสดงประเภทซ้ำ) */
export const galleryUploadDescription = (typeLabel: string, caption: string): string => {
  const trimmed = caption.trim();
  return trimmed === "" ? typeLabel : `${typeLabel}${GALLERY_DESCRIPTION_SEPARATOR}${trimmed}`;
};

/** ประเภทภาพของแถว (อ่านจาก prefix ที่ V4 เขียนไว้ก่อน แล้ว fallback แบบเดิม) */
export const galleryTypeOf = (description: string, types: string[]): string => {
  const text = `${description}`.trim();
  if (text === "") return types[0] ?? "";
  for (const type of types) {
    if (text === type || text.startsWith(`${type}${GALLERY_DESCRIPTION_SEPARATOR}`)) return type;
  }
  return galleryLabelOf(text, types);
};

/** คำอธิบายภาพจริง (ตัดประเภทภาพและ separator ออก) ไม่มีคำอธิบาย = "-" (86629 ข้อ 3.2) */
export const galleryCaptionOf = (description: string, types: string[]): string => {
  const text = `${description}`.trim();
  if (text === "") return "-";
  for (const type of types) {
    if (text === type) return "-";
    const prefix = `${type}${GALLERY_DESCRIPTION_SEPARATOR}`;
    if (text.startsWith(prefix)) {
      const rest = text.slice(prefix.length).trim();
      return rest === "" ? "-" : rest;
    }
  }
  return text;
};

export const galleryLabelOf = (description: string, types: string[]): string => {
  const text = `${description}`.toLowerCase();
  if (text === "") return types.length > 0 ? types[types.length - 1] : "";
  if (/หัตถการ|ก่อน-หลัง|pre[- ]?post/.test(text)) return types[1] ?? types[0] ?? "";
  if (/เอกสาร|ผลตรวจ|x-?ray|film|report|แลบ|lab/.test(text)) return types[2] ?? types[0] ?? "";
  return types[0] ?? "";
};

/**
 * ประวัติภาพย้อนหลังของผู้รับบริการ (issue 86631)
 * gallery API ผูกกับ emr เท่านั้น -> อ่าน encounter-history ของ HN เดียวกัน
 * (GET /apis/DPO/encounter-history/?patient= ซึ่งตอบ medical_record = id ของเวชระเบียน)
 * แล้วดึงภาพของแต่ละ encounter (จำกัดจำนวน encounter เพื่อไม่ให้ยิง request เยอะเกินจำเป็น)
 */
export const GALLERY_HISTORY_ENCOUNTER_LIMIT = 10;

export type GalleryHistoryEncounter = {
  encounterId: number;
  encounterNumber: string;
  emrId: number | null;
  dateText: string;
};

export function galleryHistoryEncountersFromApi(payload: unknown, excludeEncounterId?: number): GalleryHistoryEncounter[] {
  return itemsOf(payload)
    .map((row) => ({
      encounterId: asNumber(row.id) ?? 0,
      /* หัวกลุ่มตาม design ใช้ "เลข encounter" (Encounter.number) ไม่ใช่ id */
      encounterNumber: asText(row.number) || asText(row.id),
      emrId: asNumber(row.medical_record) ?? asNumber(row.emr) ?? null,
      dateText: asText(row.date),
    }))
    .filter((row) => row.encounterId !== 0 && row.encounterId !== excludeEncounterId)
    .slice(0, GALLERY_HISTORY_ENCOUNTER_LIMIT);
}

export type GalleryHistoryGroup = {
  encounterId: number;
  /** หัวกลุ่ม "{เลข encounter} · {วันที่}" ตาม design (86631 ข้อ 2) */
  encounterNumber: string;
  dateText: string;
  /** แถวภาพดิบของ encounter นั้น (card เป็นตัว map เป็น view เอง เหมือน photos ของ encounter ปัจจุบัน) */
  photos: GalleryApiPhoto[];
};

export function galleryHistoryGroupsOf(
  encounters: GalleryHistoryEncounter[],
  photosByEncounter: Map<number, GalleryApiPhoto[]>,
): GalleryHistoryGroup[] {
  return encounters
    .map((encounter) => ({
      encounterId: encounter.encounterId,
      encounterNumber: encounter.encounterNumber,
      dateText: encounter.dateText,
      photos: photosByEncounter.get(encounter.encounterId) ?? [],
    }))
    .filter((group) => group.photos.some((photo) => photo.active && photo.image !== ""));
}

/** อ่านชั้น annotations ที่ ClinicalPictureSerializer nested มาให้ (GET gallery) */
export function galleryAnnotationsFromApi(payload: unknown): GalleryAnnotationApi[] {
  const rows = Array.isArray(payload) ? payload : itemsOf(payload);
  const out: GalleryAnnotationApi[] = [];
  for (const row of rows) {
    const id = asNumber(row.id) ?? 0;
    if (id === 0) continue;
    const active = row.active === undefined ? row.is_active !== false : row.active !== false;
    if (!active) continue;
    const position =
      row.position !== null && typeof row.position === "object"
        ? { x: Number((row.position as Dict).x ?? 0), y: Number((row.position as Dict).y ?? 0) }
        : null;
    out.push({
      id,
      picture: asNumber(row.picture) ?? 0,
      type: row.type === "TEXT" ? "TEXT" : "PEN",
      color: asText(row.color) || "#D93025",
      width: asNumber(row.width) ?? 3,
      points: (Array.isArray(row.points) ? (row.points as unknown[]) : [])
        .map((point) => point as unknown[])
        .filter((point) => Array.isArray(point) && point.length >= 2)
        .map((point) => [Number(point[0]), Number(point[1])] as Point),
      text: asText(row.text),
      position,
      displaySeq: asNumber(row.display_seq) ?? 0,
      active: true,
      editUser: asText(row.edit_user_name),
    });
  }
  return out.sort((a, b) => a.displaySeq - b.displaySeq || a.id - b.id);
}

/** 0..1 -> px ของ canvas (reuse ตัววาดเส้นเดิมของ gallery: strokeSegments / PhotoCanvas) */
export function annotationStrokesOf(
  annotations: GalleryAnnotationApi[],
  size: { width: number; height: number },
): Stroke[] {
  return annotations
    .filter((annotation) => annotation.type === GALLERY_ANNOTATION_PEN && annotation.points.length >= 2)
    .map((annotation) => ({
      color: annotation.color,
      width: annotation.width,
      points: annotation.points.map(
        ([x, y]) => [x * size.width, y * size.height] as Point,
      ),
    }));
}

/**
 * px บน canvas -> ค่าสัมพัทธ์ 0..1 สำหรับ POST /apis/DPO/clinical-picture-annotation/
 * ข้ามเส้นที่มีจุดเดียว (วาดไม่ครบ) ออกไป
 */
export function galleryStrokesToAnnotationItems(
  strokes: Stroke[],
  pictureId: number,
  size: { width: number; height: number },
  startSeq = 0,
): Dict[] {
  return strokes
    .filter((stroke) => Array.isArray(stroke.points) && stroke.points.length >= 2)
    .map((stroke, index) => ({
      picture: pictureId,
      type: GALLERY_ANNOTATION_PEN,
      color: stroke.color,
      width: Math.max(1, Math.round(stroke.width)),
      points: stroke.points.map(([x, y]) => [
        Number((x / size.width).toFixed(5)),
        Number((y / size.height).toFixed(5)),
      ]),
      display_seq: startSeq + index,
    }));
}

export function galleryViewsFromApi(
  photos: GalleryApiPhoto[],
  args: {
    types: string[];
    doctorName?: string;
    timeOf: (iso: string) => string;
    /** ขนาด canvas ที่ใช้วาด (px) สำหรับแปลง layer 0..1 กลับเป็นจุดบนภาพ */
    canvas: { width: number; height: number };
  },
): GalleryPhotoView[] {
  return photos
    .filter((photo) => photo.active && photo.image !== "")
    .map((photo) => ({
      id: photo.id,
      label: galleryTypeOf(photo.description, args.types),
      caption: galleryCaptionOf(photo.description, args.types),
      at: args.timeOf(photo.created) || photo.created,
      by: args.doctorName ?? "",
      image: photo.image,
      /* 86630: เส้นที่บันทึกไว้เป็น layer ของ backend ถูก redraw บนภาพย่อ/ภาพขยาย
         (ไฟล์ภาพต้นฉบับไม่ถูกแก้) */
      strokes: annotationStrokesOf(photo.annotations ?? [], args.canvas),
    }));
}

/* ============================================================== */
/*   SECTION 8 · NURSE NOTE (บันทึกการพยาบาล)                      */
/* ============================================================== */

/** NurseNoteItemSerializer_apps_PTM / NurseNoteTemplateItemSerializer_apps_PTM */
export type NurseFieldKey = "focus" | "diagnosis" | "plan" | "goal";

export type NurseField = { key: NurseFieldKey; label: string };

export const NURSE_FIELDS: NurseField[] = [
  { key: "focus", label: "Focus" },
  { key: "diagnosis", label: "Nursing Diagnosis" },
  { key: "plan", label: "Plan" },
  { key: "goal", label: "Goal" },
];

/* ============================================================== */
/* Nurse Note (issue 86635 / 86636 / 86638)
   HIS V3 เก็บ Progress Note แบบ A-I-E ในคอลัมน์ diagnosis / plan / goal ของ NurseNoteItem
   (his/apps/PTM/models.py:430-456 BaseNurseNoteItem + HIS V3 CardNurseNote.tsx:177-189,283)
   Focus เก็บในคอลัมน์ `focus`                                     */
/* ============================================================== */
export const NURSE_AIE_FIELDS: { key: NurseFieldKey; label: string; short: string }[] = [
  { key: "diagnosis", label: "Assessment (A)", short: "A" },
  { key: "plan", label: "Intervention (I)", short: "I" },
  { key: "goal", label: "Evaluation (E)", short: "E" },
];

export type NurseFocusTemplate = {
  /**
   * NurseNoteTemplateItem.id — value ของ dropdown และเป็น pk ของแถวที่ต้อง PATCH/DELETE
   * (86637: เลือก Focus ที่มีอยู่เพื่อแก้/ลบต้องระบุแถวจริง ไม่ใช่แค่ id ของ template)
   */
  id: number;
  /** NurseNoteTemplate.id (parent ของ Focus, backend ตอบ field "template") */
  templateId: number | null;
  /** ชื่อ Focus = คอลัมน์ focus (86637 ข้อ 3) · fallback เป็นชื่อ template เมื่อ focus ว่าง */
  name: string;
  /** ข้อความสำเร็จรูปของแต่ละช่อง (86636 ข้อ 1: dropdown แสดง preview A / I) */
  texts: Record<NurseFieldKey, string>;
};

/** Preview ใน dropdown: "A: {Assessment} · I: {Intervention}" บรรทัดเดียว (86636 ข้อ 1) */
export const nurseFocusPreviewOf = (focus: NurseFocusTemplate): string =>
  NURSE_AIE_FIELDS.slice(0, 2)
    .map((field) => `${field.short}: ${focus.texts[field.key] ?? ""}`.trim())
    .join(" · ");

export function nurseFocusTemplatesFromApi(payload: unknown): NurseFocusTemplate[] {
  const out: NurseFocusTemplate[] = [];
  const seen = new Set<string>();

  for (const row of itemsOf(payload)) {
    const id = asNumber(row.id) ?? 0;
    const templateId = asNumber(row.template) ?? asNumber(row.template_id) ?? null;
    const texts: Record<NurseFieldKey, string> = {
      focus: decodeHtml(row.focus),
      diagnosis: decodeHtml(row.diagnosis),
      plan: decodeHtml(row.plan),
      goal: decodeHtml(row.goal),
    };
    const name = texts.focus || asText(row.template_name) || (templateId !== null ? `Template ${templateId}` : "Template");
    const key = `${id}`;
    if (id === 0 || seen.has(key)) continue;
    seen.add(key);
    out.push({ id, templateId, name, texts });
  }

  return out;
}

/* ============================================================== */
/*   86637 · ตั้งค่า Focus template (CRUD ผ่าน API ของ PTM)        */
/* ============================================================== */

/** value ของ dropdown ตอนสร้าง Focus ใหม่ (ยังไม่มี id บน server) */
export const NURSE_FOCUS_NEW = "new" as const;

export type NurseTemplateDraft = {
  itemId: number | null;
  name: string;
  texts: Record<NurseFieldKey, string>;
};

export const emptyNurseTemplateDraft = (): NurseTemplateDraft => ({
  itemId: null,
  name: "",
  texts: { focus: "", diagnosis: "", plan: "", goal: "" },
});

export const nurseTemplateDraftFromFocus = (focus: NurseFocusTemplate): NurseTemplateDraft => ({
  itemId: focus.id,
  name: focus.name,
  texts: { ...focus.texts },
});

export const NURSE_TEMPLATE_HINT = "กรอก Focus และข้อความอย่างน้อย 1 ช่อง";
export const NURSE_TEMPLATE_NEW_OPTION = "— Focus ใหม่ (ยังไม่บันทึก) —";

/** 86637 ข้อ 4: บันทึกได้เมื่อมีชื่อ Focus และข้อความ A/I/E อย่างน้อย 1 ช่อง */
export function validateNurseTemplateDraft(draft: NurseTemplateDraft): string | null {
  if (`${draft.name ?? ""}`.trim() === "") return NURSE_TEMPLATE_HINT;
  const hasText = NURSE_AIE_FIELDS.some((field) => `${draft.texts[field.key] ?? ""}`.trim() !== "");
  return hasText ? null : NURSE_TEMPLATE_HINT;
}

/** POST /apis/PTM/nurse-note-template/ — 1 Focus = 1 template item (สร้าง template ไปพร้อมกัน) */
export function nurseTemplateCreatePayload(
  draft: NurseTemplateDraft,
  divisionId?: number | null,
): Dict {
  const focus = `${draft.name ?? ""}`.trim();
  return {
    name: focus,
    ...(divisionId === undefined || divisionId === null ? {} : { divisions: [divisionId] }),
    items: [
      {
        focus,
        diagnosis: `${draft.texts.diagnosis ?? ""}`.trim(),
        plan: `${draft.texts.plan ?? ""}`.trim(),
        goal: `${draft.texts.goal ?? ""}`.trim(),
      },
    ],
  };
}

/** PATCH /apis/PTM/nurse-note-template-item/<id>/ — ส่งครบทุกช่องกันค่าเดิมหายไป */
export function nurseTemplateUpdatePayload(draft: NurseTemplateDraft): Dict {
  return {
    focus: `${draft.name ?? ""}`.trim(),
    diagnosis: `${draft.texts.diagnosis ?? ""}`.trim(),
    plan: `${draft.texts.plan ?? ""}`.trim(),
    goal: `${draft.texts.goal ?? ""}`.trim(),
    active: true,
  };
}

export const nurseTemplateSavedMessage = (name: string): string => `บันทึก Focus “${name}” แล้ว`;
export const nurseTemplateDeletedMessage = (name: string): string => `ลบ Focus “${name}” แล้ว`;

/**
 * backend ตอบ error แบบ nested ของ nested serializer เช่น
 *   {"items":[{"focus":["Focus ชื่อนี้มีอยู่ในหน่วยงานนี้แล้ว"]}]}   (POST /nurse-note-template/)
 *   {"focus":["กรุณาระบุชื่อ Focus"]}                              (PATCH /nurse-note-template-item/<id>/)
 * ดึงข้อความแรกออกมาแสดงในการ์ด
 */
export function friendlyNurseTemplateErrorText(error: unknown, fallback = "บันทึก template ไม่สำเร็จ"): string {
  const firstMessage = (value: unknown): string => {
    if (typeof value === "string") return value.trim();
    if (Array.isArray(value)) {
      for (const item of value) {
        const text = firstMessage(item);
        if (text !== "") return text;
      }
      return "";
    }
    if (value !== null && typeof value === "object") {
      for (const nested of Object.values(value as Dict)) {
        const text = firstMessage(nested);
        if (text !== "") return text;
      }
    }
    return "";
  };

  const detail = firstMessage(error);
  return detail === "" ? fallback : detail;
}

/**
 * เติมข้อความสำเร็จรูปของ Focus ลงช่อง A / I / E (86636 ข้อ 3)
 * มีข้อความอยู่แล้ว -> ต่อบรรทัดใหม่; ไม่มี -> ใส่ทั้งข้อความ (trim แล้ว)
 */
export function applyNurseFocus(
  values: Record<string, string>,
  focus: NurseFocusTemplate,
): Record<string, string> {
  const next: Record<string, string> = { ...values };
  /* ช่อง A / I / E ต้องมี value เสมอ (React controlled textarea) */
  for (const field of NURSE_AIE_FIELDS) {
    if (typeof next[field.key] !== "string") next[field.key] = "";
  }
  for (const field of NURSE_AIE_FIELDS) {
    const inject = (focus.texts[field.key] ?? "").trim();
    if (inject === "") continue;
    const current = next[field.key] ?? "";
    next[field.key] = current.trim() === "" ? inject : current + "\n" + inject;
  }
  return next;
}

/**
 * เอาข้อความของ Focus ออกจากช่อง (86636 ข้อ 5)
 * ลบได้เฉพาะส่วนที่ผู้ใช้ไม่ได้แก้ไข: ถ้าข้อความที่เหลือไม่ลงท้ายด้วยข้อความของ Focus ให้คงไว้ทั้งค่า
 * คืน {values, removed} ว่าถอด Focus ออกจากช่องไหนบ้าง
 */
export function removeNurseFocus(
  values: Record<string, string>,
  focus: NurseFocusTemplate,
): { values: Record<string, string>; removed: NurseFieldKey[] } {
  const next: Record<string, string> = { ...values };
  const removed: NurseFieldKey[] = [];

  for (const field of NURSE_AIE_FIELDS) {
    const inject = (focus.texts[field.key] ?? "").trim();
    if (inject === "") continue;
    const current = next[field.key] ?? "";
    if (current === inject) {
      next[field.key] = "";
      removed.push(field.key);
    } else if (current.endsWith("\n" + inject)) {
      next[field.key] = current.slice(0, current.length - inject.length).replace(/\n+$/, "");
      removed.push(field.key);
    }
  }

  return { values: next, removed };
}

/** ข้อความ A / I / E ของบันทึกสำหรับตาราง (86638 ข้อ 3: Progress Note แยก 3 บล็อก) */
export function nurseNoteProgressParts(note: NurseNoteApi): { short: string; text: string }[] {
  return NURSE_AIE_FIELDS.map((field) => ({
    short: field.short,
    text: note[field.key] ?? "",
  })).filter((part) => part.text.trim() !== "");
}

/**
 * HTML สำหรับ Print รายการบันทึกการพยาบาลที่เลือก (issue 86641)
 *Print ใช้ window.print ของ browser จึงต้องการ HTML string ที่ปิดตัวเอง (printEmrV4.printHtmlDocument)
 */
export function nurseNotePrintHtml(
  notes: NurseNoteApi[],
  args: { encounterNumber?: string; hn?: string; title?: string } = {},
): string {
  const rows = notes
    .map((note) => {
      const parts = nurseNoteProgressParts(note)
        .map((part) => `<div class="part"><b>${part.short}:</b> ${escapeHtml(part.text)}</div>`)
        .join("");
      return (
        "<tr>" +
        `<td>${escapeHtml(note.date)}</td>` +
        `<td>${escapeHtml(note.nurse !== "" ? note.nurse : "-")}</td>` +
        `<td><b>${escapeHtml(note.focus !== "" ? note.focus : "-")}</b></td>` +
        `<td>${parts}</td>` +
        "</tr>"
      );
    })
    .join("");

  return (
    "<table>" +
    "<thead><tr><th>วันที่และเวลาที่สร้าง</th><th>ผู้บันทึก</th><th>Focus</th><th>Progress Note (A / I / E)</th></tr></thead>" +
    `<tbody>${rows}</tbody>` +
    "</table>"
  );
}

const escapeHtml = (text: string): string =>
  `${text}`
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

/** แยก "วว/ดด/ปปปป" จากวันที่แบบ "วว/ดด/ปปปป [ชช:นน]" หรือ "วว/ดด/ปปปป ชช:นน" */
export const beDatePartOf = (text: string): string => `${text}`.trim().split(/[\s[]/)[0];

/**
 * แปลงวันที่หลายรูปแบบเป็นคีย์เปรียบเทียบได้ (ค.ศ.)
 * - backendไทยตอบ "วว/ดด/ปปปป" หรือ "วว/ดด/ปปปป [ชช:นน]" (พุทธศักราช)
 * - ISO จาก EditableModel.created = "ปปปป-ดด-ววTชช:นน:.ssZ"
 */
const toComparableDate = (text: string): string => {
  const part = beDatePartOf(text);

  const be = part.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (be !== null) {
    const year = Number(be[3]);
    /* ปี พ.ศ. (>=2300) แปลงเป็น ค.ศ. เพื่อเทียบกับ ISO ของ backend */
    const ad = year >= 2300 ? year - 543 : year;
    return `${String(ad).padStart(4, "0")}-${be[2].padStart(2, "0")}-${be[1].padStart(2, "0")}`;
  }

  const iso = part.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso !== null) return `${iso[1]}-${iso[2]}-${iso[3]}`;

  return "";
};

/** กรองรายการตามช่วงวันที่ค้นหา (86638 ข้อ 4: เติมได้ทั้ง 2 ข้าง ไม่กรอง = แสดงทั้งหมด) */
export function nurseNotesInRange(notes: NurseNoteApi[], from: string, to: string): NurseNoteApi[] {
  const inRange = (note: NurseNoteApi): boolean => {
    const day = toComparableDate(note.created !== "" ? note.created : note.date);
    if (day === "") return true;
    const left = toComparableDate(from);
    const right = toComparableDate(to);
    if (left !== "" && day < left) return false;
    if (right !== "" && day > right) return false;
    return true;
  };
  return notes.filter(inRange);
}

/** เรียงใหม่สุดก่อน (86638 ข้อ 3) โดยใช้ created (ISO) แล้ว fallback วัน/เวลาของรายการ */
export function sortNurseNotesNewestFirst(notes: NurseNoteApi[]): NurseNoteApi[] {
  const keyOf = (note: NurseNoteApi): string =>
    toComparableDate(note.created !== "" ? note.created : note.date);
  return notes.slice().sort((a, b) => (keyOf(a) < keyOf(b) ? 1 : keyOf(a) > keyOf(b) ? -1 : b.id - a.id));
}

export type NurseFocusTemplateOld = NurseTemplateApi;

export type NurseTemplateApi = {
  /** template id (NurseNoteTemplateItemSerializer.template) */
  id: number | null;
  name: string;
  fields: NurseField[];
};

export type NurseNoteApi = {
  id: number;
  nurseNoteId: number | null;
  date: string;
  nurse: string;
  nurseCode: string;
  shift: string;
  divisionName: string;
  doctorOrderId: string | null;
  doctorOrderDetail: string;
  focus: string;
  diagnosis: string;
  plan: string;
  goal: string;
  active: boolean;
  /** ISO จาก EditableModel (his/framework/models/models.py:42-44) ใช้กรองช่วงวันที่ + แสดง "แก้ไขครั้งล่าสุด" */
  created: string;
  edited: string;
  /** ชื่อผู้แก้ไขล่าสุด (edit_user.get_full_name) */
  editor: string;
};

/**
 * GET /apis/PTM/nurse-note-template-item/?division=
 * แถว 1 แถว = 1 ช่องของ template; group ด้วย field `template`
 * ชื่อ template ไม่มีใน serializer -> ใช้ template_name ถ้า backend ส่งมา มิฉะนั้นใช้ "Template <id>"
 */
export function nurseTemplatesFromApi(payload: unknown): NurseTemplateApi[] {
  const groups = new Map<string, NurseTemplateApi & { keys: Set<NurseFieldKey> }>();

  for (const row of itemsOf(payload)) {
    const templateId = asNumber(row.template) ?? asNumber(row.template_id) ?? asNumber(row.id) ?? null;
    const name = asText(row.template_name) || (templateId !== null ? `Template ${templateId}` : "Template");
    const key = `${templateId ?? name}`;
    const group = groups.get(key) ?? { id: templateId, name, fields: [], keys: new Set<NurseFieldKey>() };

    for (const field of NURSE_FIELDS) {
      if (row[field.key] !== undefined && row[field.key] !== null) group.keys.add(field.key);
    }
    groups.set(key, group);
  }

  return Array.from(groups.values()).map((group) => ({
    id: group.id,
    name: group.name,
    /* template ที่ backend ไม่ได้แบ่งคอลัมน์ ใช้ครบ 4 ช่อง (DAR) */
    fields: NURSE_FIELDS.filter((field) => group.keys.has(field.key)),
  }));
}

export function nurseNotesFromApi(payload: unknown): NurseNoteApi[] {
  return itemsOf(payload).map((row) => ({
    id: asNumber(row.id) ?? 0,
    nurseNoteId: asNumber(row.nurse_note) ?? null,
    date: asText(row.formatted_date) || asText(row.formatted_date_raw) || asText(row.date),
    nurse: asText(row.nurse),
    nurseCode: asText(row.nurse_code),
    shift: asText(row.working_shift),
    divisionName: asText(row.division_name),
    doctorOrderId: asText(row.doctor_order_id) || null,
    doctorOrderDetail: decodeHtml(row.doctor_order_detail),
    focus: decodeHtml(row.focus),
    diagnosis: decodeHtml(row.diagnosis),
    plan: decodeHtml(row.plan),
    goal: decodeHtml(row.goal),
    active: row.active === undefined ? true : asBoolean(row.active),
    created: asText(row.created) || asText(row.formatted_date_raw),
    edited: asText(row.edited) || "",
    editor: asText(row.edit_user) || "",
  }));
}

/** รวมช่องของ note เป็นบรรทัดเดียวแบบที่ V4 แสดง (label ต่อหน้าค่าเมื่อมีมากกว่า 1 ช่อง) */
export function nurseNoteText(note: NurseNoteApi): string {
  const parts = NURSE_FIELDS.map((field) => ({ field, value: note[field.key] })).filter((item) => item.value !== "");
  if (parts.length === 0) return "";
  if (parts.length === 1) return parts[0].value;
  return parts.map((item) => `${item.field.label}: ${item.value}`).join(" / ");
}

/** date ที่ backend ต้องการ: "DD/MM/YYYY-HH:mm" พ.ศ. (PTMI.ts:781-805) */
/** "วว/ดด/ปปปป" ของวันปัจจุบัน (พุทธศักราช) — ค่าเริ่มต้นของช่องตั้งแต่วันที่/ถึงวันที่ (86638 ข้อ 4) */
export const beTodayText = (): string => {
  const now = new Date();
  const dd = String(now.getDate()).padStart(2, "0");
  const mm = String(now.getMonth() + 1).padStart(2, "0");
  return `${dd}/${mm}/${String(now.getFullYear() + 543)}`;
};

export const nurseDateOfNow = (): string => {
  const now = new Date();
  const dd = String(now.getDate()).padStart(2, "0");
  const mm = String(now.getMonth() + 1).padStart(2, "0");
  const hh = String(now.getHours()).padStart(2, "0");
  const mi = String(now.getMinutes()).padStart(2, "0");
  return `${dd}/${mm}/${String(now.getFullYear() + 543)}-${hh}:${mi}`;
};

/**
 * POST /apis/PTM/nurse-note-item/ (PTMInterface.ts:515-535)
 * date ต้องเป็น "DD/MM/YYYY-HH:mm" แบบที่ V3 ประกอบ (PTMI.ts:781-805)
 */
export function buildNurseNotePayload(args: {
  encounterId?: number;
  nurseNoteId?: number | null;
  division?: number;
  date: string;
  values: Partial<Record<NurseFieldKey, string>>;
  /** ผู้บันทึก: username/password (V3 PTMI.ts) หรือ user_token (HISV3 PTMInterface.ts:515-535) */
  auth?: { username?: string; password?: string; user_token?: string };
  /** เวรที่บันทึก (`working_shift`) — V3 มีช่องนี้เฉพาะ IPD (SubNurseNoteItem.tsx:176-191, PTMI.ts:793-795) */
  workingShift?: string;
  /** order ที่บันทึกนี้ผูกด้วย (`doctor_order_id`) — V3 ใช้ตอนบันทึกจาก QA chat (PTMI.ts:808-810) */
  doctorOrderId?: number | string | null;
}): Dict {
  const draft = args.values;
  const payload: Dict = {
    date: args.date,
    focus: draft.focus ?? "",
    diagnosis: draft.diagnosis ?? "",
    plan: draft.plan ?? "",
    goal: draft.goal ?? "",
  };
  if (args.encounterId !== undefined) payload.encounter = args.encounterId;
  payload.nurse_note_id = args.nurseNoteId ?? null;
  if (args.division !== undefined) payload.division = args.division;
  /* V3 ส่งเฉพาะช่องที่มีค่า (if workingShift) — sending empty would overwrite the stored shift */
  const shift = `${args.workingShift ?? ""}`.trim();
  if (shift !== "") payload.working_shift = shift;
  if (args.doctorOrderId !== undefined && args.doctorOrderId !== null && `${args.doctorOrderId}` !== "") {
    payload.doctor_order_id = args.doctorOrderId;
  }
  if (args.auth?.username !== undefined) payload.username = args.auth.username;
  if (args.auth?.password !== undefined) payload.password = args.auth.password;
  if (args.auth?.user_token !== undefined) payload.user_token = args.auth.user_token;
  return payload;
}

/**
 * PATCH body ของ nurse-note-item: เฉพาะ field ที่แก้จริง + ผู้บันทึก
 * (V3 ใช้ PUT ทั้ง record: PTMI.ts:833-883 — V4 ไม่ส่ง field ที่ไม่มีใน UI เพื่อไม่ให้ค่าเดิมหาย)
 */
export function buildNurseNoteUpdatePayload(args: {
  values?: Partial<Record<NurseFieldKey, string>>;
  workingShift?: string;
  doctorOrderId?: number | string | null;
  auth?: { username?: string; password?: string; user_token?: string };
}): Dict {
  const payload: Dict = {};
  for (const [key, value] of Object.entries(args.values ?? {})) {
    payload[key] = `${value ?? ""}`.trim();
  }
  const shift = `${args.workingShift ?? ""}`.trim();
  if (shift !== "") payload.working_shift = shift;
  if (args.doctorOrderId !== undefined && args.doctorOrderId !== null && `${args.doctorOrderId}` !== "") {
    payload.doctor_order_id = args.doctorOrderId;
  }
  if (args.auth?.username !== undefined) payload.username = args.auth.username;
  if (args.auth?.password !== undefined) payload.password = args.auth.password;
  if (args.auth?.user_token !== undefined) payload.user_token = args.auth.user_token;
  return payload;
}

/** GET /apis/PTM/nurse-note-latest/?encounter= → id ของ record หัว (NurseNoteSerializerForCreate_apps_PTM) */
export function nurseNoteHeadIdFromApi(payload: unknown): number | null {
  const data = asDict(payload);
  return asNumber(data.id) ?? null;
}

/* ============================================================== */
/*   SECTION 9 · VITAL SIGN (ข้อมูลจากการคัดกรอง)                   */
/* ============================================================== */

/** VitalSignValueListSerializer_apps_PTM */
export type VitalChip = {
  id: number;
  code: string;
  name: string;
  result: string;
  measurement: number | null;
  unit: string;
  displaySeq: number;
  level: string;
  /** ผู้วัด (measure_edit_user_fullname) = "ชื่อผู้คัดกรอง" ในหัวการ์ดสรุป (86623 ข้อ 1) */
  editUser: string;
  /** เวลาที่วัด (ISO) */
  created: string;
};

export function vitalsFromApi(payload: unknown): VitalChip[] {
  return itemsOf(payload)
    .map((row) => ({
      id: asNumber(row.id) ?? 0,
      code: asText(row.vitalsign_type_code),
      name: asText(row.name),
      result: asText(row.result),
      measurement: asNumber(row.measurement) ?? null,
      unit: asText(row.unit),
      displaySeq: asNumber(row.display_seq) ?? 0,
      level: asText(row.level),
      editUser: asText(row.measure_edit_user_fullname ?? row.edit_user_fullname),
      created: asText(row.created ?? row.edited),
    }))
    .sort((a, b) => a.displaySeq - b.displaySeq);
}

export const vitalLabel = (chip: VitalChip): string => {
  const value = chip.result !== "" ? chip.result : chip.measurement === null ? "" : `${chip.measurement}`;
  return `${chip.name}${value ? ` ${value}${chip.unit ? ` ${chip.unit}` : ""}` : ""}`.trim();
};

/* ============================================================== */
/*   การ์ด "สรุปจากการคัดกรอง" ของหน้าตรวจรักษา (issue 86623)      */
/* ============================================================== */

/**
 * code vital sign ที่ API ส่งมาต่างหน่วยงานไม่เหมือนกันทั้งหมด (SP/DP/PR/BT/O2Sat/PS ...)
 * -> code UI ของหน้าคัดกรอง เพื่อใช้เกณฑ์ค่าผิดปกติ (S4 / issue 86617) ชุดเดียวกัน
 */
export const VITAL_API_CODE_TO_UI: Record<string, string> = {
  BT: "TEMP",
  TEMP: "TEMP",
  SP: "BP_SYS",
  SBP: "BP_SYS",
  BP_SYS: "BP_SYS",
  DP: "BP_DIA",
  DBP: "BP_DIA",
  BP_DIA: "BP_DIA",
  PR: "PULSE",
  PULSE: "PULSE",
  RR: "RR",
  O2Sat: "SPO2",
  SPO2: "SPO2",
  O2: "SPO2",
  PS: "PAIN_SCORE",
  "Pain Score": "PAIN_SCORE",
  PAIN_SCORE: "PAIN_SCORE",
};

export const vitalValueOf = (chip: VitalChip): string => {
  const result = chip.result.trim();
  if (result !== "") return result;
  return chip.measurement === null ? "" : `${chip.measurement}`;
};

const vitalByCode = (vitals: VitalChip[], ...codes: string[]): VitalChip | undefined =>
  vitals.find((chip) => codes.some((code) => code.toLowerCase() === chip.code.trim().toLowerCase()));

/** ระดับความผิดปกติ: ใช้เกณฑ์ของหน้าคัดกรองก่อน แล้วfallback เป็น level ของ backend */
export const vitalSeverityLevelOf = (chip?: VitalChip): 0 | 1 | 2 => {
  if (!chip) return 0;
  const code = chip.code.trim();
  const judged = vitalSeverityOf(VITAL_API_CODE_TO_UI[code] ?? code, vitalValueOf(chip));
  if (judged !== null && judged.level > 0) return judged.level;
  const level = chip.level.trim();
  if (level === "" || level === "ปกติ") return 0;
  return 1;
};

const chipToneOf = (level: number): ExamChipTone => (level >= 2 ? "danger" : level === 1 ? "warn" : "neutral");

export const triageLevelNameOf = (level: string): string =>
  (TRIAGE_LEVELS.find((def) => def.level === String(level).trim()) || { name: "" }).name;

/** แถว ProblemList ที่เป็น "โรคประจำตัว" (V4 ขึ้นต้น message ด้วยหมวดหมู่; แถวไม่มีคำขึ้นต้นถือเป็นโรคประจำตัว) */
export const diseaseNamesOf = (problemItems: ProblemItem[]): string[] =>
  problemItems
    .map((item) => {
      const message = item.message.trim();
      if (message === "") return "";
      const prefixed = message.match(/^(โรคประจำตัว|ยาที่ใช้ประจำ|ประวัติผ่าตัด|สูบบุหรี่|ดื่มสุรา):\s*(.*)$/);
      if (prefixed) return prefixed[1] === "โรคประจำตัว" ? prefixed[2].trim() : "";
      return message;
    })
    .filter((name) => name !== "");

/**
 * chip สรุปจากการคัดกรอง ตาม issue 86623 ข้อ 2-4
 * อุณหภูมิ · BP (Systolic/Diastolic) · ชีพจร · O2 Sat · Pain · Triage (เลข + ชื่อระดับ)
 * · ประวัติแพ้ (ชื่อ + อาการ) · โรคประจำตัว — ไม่มีค่า = ไม่มี chip ของค่านั้น
 */
export function screeningSummaryChips({
  vitals,
  triageLevel,
  allergyItems,
  problemItems,
}: {
  vitals: VitalChip[];
  triageLevel: string;
  allergyItems: AllergyItem[];
  problemItems: ProblemItem[];
}): ExamSummaryChip[] {
  const chips: ExamSummaryChip[] = [];

  const temp = vitalByCode(vitals, "BT", "TEMP");
  if (temp && vitalValueOf(temp) !== "") {
    chips.push({ text: `อุณหภูมิ ${vitalValueOf(temp)} °C`, tone: chipToneOf(vitalSeverityLevelOf(temp)) });
  }

  const sys = vitalByCode(vitals, "SP", "SBP", "BP_SYS");
  const dia = vitalByCode(vitals, "DP", "DBP", "BP_DIA");
  if (sys && dia && vitalValueOf(sys) !== "" && vitalValueOf(dia) !== "") {
    const level = Math.max(vitalSeverityLevelOf(sys), vitalSeverityLevelOf(dia));
    chips.push({ text: `BP ${vitalValueOf(sys)}/${vitalValueOf(dia)}`, tone: chipToneOf(level) });
  } else {
    /* บางหน่วยตรวจเก็บ BP เป็นแถวเดียว ("120/80") -> ใช้แถวนั้นแทน */
    const bp = vitalByCode(vitals, "BP");
    if (bp && vitalValueOf(bp) !== "") {
      chips.push({ text: `BP ${vitalValueOf(bp)}`, tone: chipToneOf(vitalSeverityLevelOf(bp)) });
    }
  }

  const pulse = vitalByCode(vitals, "PR", "PULSE");
  if (pulse && vitalValueOf(pulse) !== "") {
    chips.push({ text: `ชีพจร ${vitalValueOf(pulse)}`, tone: chipToneOf(vitalSeverityLevelOf(pulse)) });
  }

  const spo2 = vitalByCode(vitals, "O2Sat", "SPO2", "O2");
  if (spo2 && vitalValueOf(spo2) !== "") {
    chips.push({ text: `O2 Sat ${vitalValueOf(spo2)}%`, tone: chipToneOf(vitalSeverityLevelOf(spo2)) });
  }

  const pain = vitalByCode(vitals, "PS", "Pain Score", "PAIN_SCORE");
  if (pain && vitalValueOf(pain) !== "") {
    chips.push({ text: `Pain ${vitalValueOf(pain)}/10`, tone: chipToneOf(vitalSeverityLevelOf(pain)) });
  }

  const level = String(triageLevel ?? "").trim();
  if (level !== "") {
    const name = triageLevelNameOf(level);
    chips.push({ text: `Triage ${level}${name ? ` · ${name}` : ""}`, tone: "neutral" });
  }

  /* ประวัติแพ้: พื้นแดงอ่อนเสมอ (issue ข้อ 4) */
  allergyItems
    .filter((item) => item.name.trim() !== "")
    .forEach((item) => {
      const symptom = item.note.trim();
      chips.push({ text: `แพ้ ${item.name}${symptom ? ` (${symptom})` : ""}`, tone: "danger" });
    });

  diseaseNamesOf(problemItems).forEach((name) => chips.push({ text: `${name} (โรคประจำตัว)`, tone: "neutral" }));

  return chips;
}

/** หัวการ์ด "สรุปจากการคัดกรอง ({ชื่อผู้คัดกรอง} · {เวลา})" จากผู้วัด vital sign ของ Encounter นี้ */
export function screeningSummaryTitle(vitals: VitalChip[]): string {
  const user = vitals.map((chip) => chip.editUser.trim()).find((name) => name !== "") ?? "";
  const created = vitals.map((chip) => chip.created.trim()).find((value) => value !== "") ?? "";
  const time = created === "" ? "" : beDateTimeText(created);
  const parts = [user, time].filter((part) => part !== "");
  return parts.length > 0 ? `สรุปจากการคัดกรอง (${parts.join(" · ")})` : "สรุปจากการคัดกรอง (ล่าสุด)";
}

/* ============================================================== */
/*   SECTION 10 · ORGAN MASTER (fallback เมื่อ API ไม่มี master)    */
/* ============================================================== */

export const EXAM_NOT_APPLICABLE = "N/A";

export type OrganMaster = { organId: number | null; organName: string; options: string[] };

/**
 * id ของแพทย์ผู้ตรวจบน encounter (V3: `selectedEncounter?.approve_by || selectedEncounter?.doctor`
 *   HISV3/common/CardPatientListInterface.ts:1485)
 * สำคัญกับ START_EXAM: seed patient_illness ต้องส่ง `doctor` id ไม่งั้นผู้ป่วยไม่ถูกย้ายเป็น IN_EXAM
 */
export function doctorIdOf(encounter?: Dict | null, emr?: Dict | null): number | null {
  return (
    asNumber(encounter?.approve_by) ??
    asNumber(encounter?.doctor) ??
    asNumber(encounter?.doctor_id) ??
    asNumber(emr?.doctor) ??
    null
  );
}

/** emr id: selectedEmr.id ก่อน แล้วค่อย fallback ไป field ที่ encounter มี */
export function emrIdOf(emr?: Dict | null, encounter?: Dict | null): number | null {
  return (
    asNumber(emr?.id) ??
    asNumber(emr?.emr) ??
    asNumber(encounter?.emr) ??
    asNumber(encounter?.medical_record) ??
    null
  );
}

/**
 * selectedEmr ใน state กลางไม่ถูกล้างตอนเปลี่ยนผู้ป่วย (ค้างจนกว่า SelectEncounter จะโหลดเสร็จ)
 * ใช้ได้เฉพาะเมื่อเป็น EMR ของ encounter ที่ถาม ไม่รู้ encounter ของ EMR = ไม่ใช้
 */
export function emrOfEncounter<T extends object>(
  emr?: T | null,
  encounter?: { id?: unknown } | null,
): T | null {
  const encounterId = asNumber(encounter?.id);
  return emr && encounterId !== undefined && asNumber(asDict(emr).encounter) === encounterId ? emr : null;
}

export function organMasterFromApi(payload: unknown): OrganMaster[] {
  return itemsOf(payload).map((row) => ({
    organId: asNumber(row.organ_id) ?? asNumber(row.id) ?? null,
    organName: asText(row.organ_name) || asText(row.name),
    options: (Array.isArray(row.test_result_option_list) ? row.test_result_option_list : [])
      .map(trimOption)
      .filter((option) => option !== ""),
  }));
}

/* ============================================================== */
/*   SECTION 10a · PATIENT PANEL (สมาชิก + นัดครั้งหน้า)            */
/* ============================================================== */

/** ระดับสมาชิกจาก CRM/loyalty-account + CRM/loyalty-tier */
export type MemberInfo = {
  id: number;
  tier: string;
  /** ลำดับของระดับ (เรียงตาม start_point) ใช้เลือกสี 0-3 */
  tierIndex: number;
  nextTier: string;
  /** ยอดที่ต้องซื้อเพิ่มถึงระดับถัดไป */
  need: number;
  /** progress ไประดับถัดไป 0-100 */
  percent: number;
  /** ยอดซื้อสะสม (tier_points) */
  spend: number;
  points: number;
  /** คะแนนที่หมดอายุภายใน EXPIRE_SOON_DAYS วัน */
  soonPoints: number;
  soonDate: string;
};

export const EXPIRE_SOON_DAYS = 90;

/** state ของ BillPaymentSequence หลังออกใบเสร็จสำเร็จ (BillPayment.CreateReceipt) */
export function isReceiptCreated(billPayment: unknown): boolean {
  if (!billPayment || typeof billPayment !== "object") return false;
  const keys = Object.keys(billPayment);
  return keys.length === 1 && (billPayment as { openCardPaying?: unknown }).openCardPaying === true;
}

/** บัญชีสมาชิกของผู้ป่วย (หาจาก field patient) ไม่มีบัญชี = null */
export function memberFromApi(
  accounts: unknown,
  tiers: unknown,
  patientId: number | undefined,
  today: Moment,
): MemberInfo | null {
  const account = itemsOf(accounts).find((row) => asNumber(row.patient) === patientId);
  if (patientId === undefined || account === undefined) return null;

  const current = asDict(account.current_tier);
  const next = asDict(account.next_tier);
  const tierIds = itemsOf(tiers)
    .sort((a, b) => (asNumber(a.start_point) ?? 0) - (asNumber(b.start_point) ?? 0))
    .map((row) => asNumber(row.id));
  const tierIndex = Math.max(0, tierIds.indexOf(asNumber(current.id)));

  const spend = asNumber(account.tier_points) ?? 0;
  const start = asNumber(current.start_point) ?? 0;
  const nextStart = asText(next.name) !== "" ? asNumber(next.start_point) : undefined;

  const limit = today.clone().add(EXPIRE_SOON_DAYS, "days");
  const soon = asList(account.next_expire_reward_points)
    .map((row) => ({
      amount: asNumber(row.total_amount) ?? 0,
      at: moment(asText(row.expired_at), moment.ISO_8601),
    }))
    .filter((row) => row.amount > 0 && row.at.isValid() && row.at.isSameOrBefore(limit, "day"))
    .sort((a, b) => a.at.valueOf() - b.at.valueOf());

  return {
    id: asNumber(account.id) ?? 0,
    tier: asText(current.name),
    tierIndex: Math.min(tierIndex, 3),
    nextTier: nextStart === undefined ? "" : asText(next.name),
    need: nextStart === undefined ? 0 : Math.max(0, nextStart - spend),
    percent:
      nextStart === undefined
        ? 100
        : Math.min(
            100,
            Math.max(0, Math.round(((spend - start) * 100) / (nextStart - start || 1))),
          ),
    spend,
    points: asNumber(account.reward_points) ?? 0,
    soonPoints: soon.reduce((sum, row) => sum + row.amount, 0),
    soonDate: soon.length ? formatDate(soon[0].at) : "",
  };
}

/** ชื่อจาก list ของ ClinicalTerm ใน encounter (channel_data_list / regularly_taken_supplement_data_list) */
export const termNamesOf = (value: unknown): string[] =>
  asList(value)
    .map((row) => asText(row.name))
    .filter((name) => name !== "");

/** สถานะนัดที่ไม่ใช่นัดถัดไป: 3 ยกเลิก, 5 เปิด Encounter แล้ว */
/** สถานะนัดของ QUE ที่ยังรอมาตามนัด: REQUEST, CONFIRM, REVISE, RECONFIRM */
export const OPEN_APPOINTMENT_STATUS = "1,2,3,4";

/** นัดหมายถัดไปที่ใกล้ที่สุดจาก QUE/patient-appointment/v2/
    (estimated_at "DD/MM/YYYY [HH:mm]" -> "DD/MM/YYYY HH:mm" หรือ "" เมื่อไม่มี) */
export function nextAppointmentFromApi(payload: unknown, today: Moment): string {
  const upcoming: { date: string; day: Moment; time: string }[] = [];
  for (const row of itemsOf(payload)) {
    if (!OPEN_APPOINTMENT_STATUS.split(",").includes(String(row.status))) continue;
    const [date = "", time = ""] = asText(row.estimated_at).replace(/[[\]]/g, "").split(/\s+/);
    const day = parseDate(date, true);
    if (!day?.isValid() || day.isBefore(today, "day")) continue;
    upcoming.push({ date, day, time: time.slice(0, 5) });
  }
  const next = upcoming.sort(
    (a, b) => a.day.valueOf() - b.day.valueOf() || a.time.localeCompare(b.time),
  )[0];
  return next ? `${next.date}${next.time ? ` ${next.time}` : ""}` : "";
}

/* ============================================================== */
/*   SECTION 9b · errorTextOf (error จาก SDK -> ข้อความไทย)         */
/* ============================================================== */

/** helper state/error กลางของ HIS V4 (re-export ไว้ให้ import เดิมใช้ได้) */
export { errorTextOf } from "../common/errorTextV4";

/* ============================================================== */
/*   SECTION 10 · ExamV4Slice (state ของ sequence)                 */
/* ============================================================== */

/**
 * state ของ sequence "ExamV4" ที่ MainHIS ส่งกลับลงมาให้เป็น prop ของ card
 *
 * ไม่มี loading/error ใน slice — ทั้งสองอย่างเป็น state กลางของ app ตาม practice ของทีม
 * (`buttonLoadCheck[card_action]` + `errorMessage[card]` ผ่าน BLC_STATUS / SetErrorMessage
 *  ดู common/sequence/PatientOrdersV4.ts และ HISV3/common/CommonInterface.ts)
 */
export type RecordSaveMode = "draft" | "confirm";

export type RecordSaveFailure = {
  section: "exam" | "history" | "diagnosis" | "reload" | "status";
  message: string;
};

export type RecordSaveResult = {
  requestId: string;
  mode: RecordSaveMode;
  ok: boolean;
  failed: RecordSaveFailure[];
};

export type ExamV4Slice = {
  sequenceIndex: string | null;
  ids: ExamIds;
  /** เวลา (ISO) ที่ save สำเร็จล่าสุดของทุกหน้าใน sequence (gallery/nursing ใช้เป็น key reload) */
  savedAt: string | null;
  /** เวลา (ISO) ที่บันทึกเวชระเบียนของหน้าตรวจรักษาล่าสุด ใช้แสดงใน UI */
  recordSavedAt: string | null;
  /** ผลของ SAVE_RECORD ล่าสุด card ใช้ตัดสินสถานะ Confirm หลัง server ตอบครบ */
  recordSave: RecordSaveResult | null;
  subjective: Subjective;
  /** แถวตรวจร่างกายจาก master organ + แถวที่เก็บไว้ */
  examRows: ExamRow[];
  /** แถวของทุก template ที่เลือกได้ (master + ที่เคยบันทึก) */
  templateRows: ExamRow[];
  /** template ที่ EMR นี้บันทึกไว้แล้ว */
  selectedTemplateIds: number[];
  other: ExamOther;
  diagnosis: DiagnosisState;
  /** flag ต้องอนุมัติ/อนุมัติแล้ว (ใช้กับ gate Confirm ของหน้าตรวจรักษา) */
  diagnosisApproval: DiagnosisApproval;
  /** record ของบันทึกเฉพาะ (sensitive note) — อ่านพร้อม LOAD, ใช้ id ตัดสิน PATCH vs สร้างใหม่ */
  sensitiveNoteRecord: SensitiveNoteRecord;
  /** ผลค้นหา ICD-10 ล่าสุด (หน้าเดียว 6 รายการเหมือน V3) */
  icdResults: IcdHit[];
  icdQuery: string;
  triage: TriageInfo;
  /** chip อาการนำ + vital sign อ่านได้ทางเดียว */
  vitals: VitalChip[];
  photos: GalleryApiPhoto[];
  /** ประวัติภาพของ Encounter อื่นของผู้ป่วยเดียวกัน (86631) — โหลดเมื่อเปิดส่วน History */
  galleryHistory: GalleryHistoryGroup[];
  /** รูปแบบภาพของคลินิก (template-gallery) ใช้กับปุ่ม เลือกรูปแบบ */
  galleryTemplates: GalleryTemplateItem[];
  /** ข้อความสรุปผลบันทึก/ลบชั้นเส้นที่วาดบนภาพ (86630) */
  gallerySavedMessage: string;
  nurseTemplates: NurseTemplateApi[];
  /** Focus charting ที่เลือกได้ (86636) — อ่านจาก nurse-note-template-item เช่นกัน */
  nurseFocusTemplates: NurseFocusTemplate[];
  nurseNotes: NurseNoteApi[];
  /** ข้อความสรุปผลบันทึก/แก้ไข/ลบ Nurse Note (86635 / 86639 / 86640) */
  nurseSavedMessage: string;
  /** ข้อ error ของ Focus template CRUD (86637) - แสดงในกล่องตั้งค่า template ตรง ๆ
     ไม่ไปปนกับ error bar ของการอ่าน/บันทึก Nurse Note (ที่เคยขึ้น prefix "อ่านบันทึกการพยาบาลไม่สำเร็จ") */
  nurseTemplateError: string;
  /** id ของ nurse-note head ล่าสุดของ encounter (POST nurse-note-item ต้องส่ง id ไปด้วย) */
  nurseNoteHeadId: number | null;
  /** ประวัติแพ้ (ใช้กับ Patient Panel flag "แพ้") */
  allergyItems: AllergyItem[];
  /** โรคประจำตัว (ใช้กับ Patient Panel flag "โรคประจำตัว") */
  problemItems: ProblemItem[];
  /** ระดับสมาชิกของผู้ป่วย (null = ยังไม่เป็นสมาชิก / ยังไม่โหลด) */
  member: MemberInfo | null;
  /** นัดครั้งหน้า "DD/MM/YYYY HH:mm" ("" = ไม่มีนัด) */
  nextAppointment: string;
  /** ช่องทางที่รู้จักคลินิก / ยาที่ใช้ประจำ จาก encounter detail */
  channels: string[];
  latestChannel?: TeamMessageRecord;
  specialRequests?: TeamMessageRecord;
  teamMessageErrors: string[];
  medications: string[];
  /** สิทธิการรักษาของ encounter */
  coverage: string;
  /** โหลด member + นัดครั้งหน้าแล้ว (แยกจาก "ยังไม่เป็นสมาชิก") */
  panelLoaded: boolean;
  /** ข้อมูล Check out ของ encounter ล่าสุด */
  checkout: CheckoutEncounter | null;
};

/**
 * state ของ sequence อาจถูก RunSequence ล้างเหลือแค่ { sequenceIndex: "START" } ชั่วคราว
 * (MainHISInterface.ts:700-712 ก่อนเรียก START handler) -> card ต้องอ่านได้โดยไม่ crash
 */
export const normalizeExamV4Slice = (
  slice: Partial<ExamV4Slice> | null | undefined,
): ExamV4Slice => {
  if (slice === null || slice === undefined) return emptyExamV4Slice();

  const base = emptyExamV4Slice();

  return {
    ...base,
    ...slice,
    ids: slice.ids ?? base.ids,
    subjective: slice.subjective ?? base.subjective,
    examRows: Array.isArray(slice.examRows) ? slice.examRows : base.examRows,
    templateRows: Array.isArray(slice.templateRows) ? slice.templateRows : base.templateRows,
    selectedTemplateIds: Array.isArray(slice.selectedTemplateIds)
      ? slice.selectedTemplateIds
      : base.selectedTemplateIds,
    other: slice.other ?? base.other,
    diagnosis: slice.diagnosis ?? base.diagnosis,
    diagnosisApproval: slice.diagnosisApproval ?? base.diagnosisApproval,
    sensitiveNoteRecord: slice.sensitiveNoteRecord ?? base.sensitiveNoteRecord,
    icdResults: Array.isArray(slice.icdResults) ? slice.icdResults : base.icdResults,
    triage: slice.triage ?? base.triage,
    vitals: Array.isArray(slice.vitals) ? slice.vitals : base.vitals,
    photos: Array.isArray(slice.photos) ? slice.photos : base.photos,
    galleryHistory: Array.isArray(slice.galleryHistory) ? slice.galleryHistory : base.galleryHistory,
    galleryTemplates: Array.isArray(slice.galleryTemplates) ? slice.galleryTemplates : base.galleryTemplates,
    nurseTemplates: Array.isArray(slice.nurseTemplates) ? slice.nurseTemplates : base.nurseTemplates,
    nurseFocusTemplates: Array.isArray(slice.nurseFocusTemplates)
      ? slice.nurseFocusTemplates
      : base.nurseFocusTemplates,
    nurseNotes: Array.isArray(slice.nurseNotes) ? slice.nurseNotes : base.nurseNotes,
    allergyItems: Array.isArray(slice.allergyItems) ? slice.allergyItems : base.allergyItems,
    problemItems: Array.isArray(slice.problemItems) ? slice.problemItems : base.problemItems,
    member: slice.member ?? base.member,
    nextAppointment:
      typeof slice.nextAppointment === "string" ? slice.nextAppointment : base.nextAppointment,
    channels: Array.isArray(slice.channels) ? slice.channels : base.channels,
    teamMessageErrors: Array.isArray(slice.teamMessageErrors) ? slice.teamMessageErrors : base.teamMessageErrors,
    medications: Array.isArray(slice.medications) ? slice.medications : base.medications,
    coverage: typeof slice.coverage === "string" ? slice.coverage : base.coverage,
    panelLoaded: slice.panelLoaded === true,
    checkout: slice.checkout ?? base.checkout,
  };
};

export const emptyExamV4Slice = (): ExamV4Slice => ({
  sequenceIndex: null,
  ids: {},
  savedAt: null,
  recordSavedAt: null,
  recordSave: null,
  subjective: SubjectiveInitial,
  examRows: [],
  templateRows: [],
  selectedTemplateIds: [],
  other: ExamOtherInitial,
  diagnosis: DiagnosisInitial,
  diagnosisApproval: DiagnosisApprovalInitial,
  sensitiveNoteRecord: SensitiveNoteRecordInitial,
  icdResults: [],
  icdQuery: "",
  triage: TriageInitial,
  vitals: [],
  photos: [],
  galleryHistory: [],
  galleryTemplates: [],
  gallerySavedMessage: "",
  nurseTemplates: [],
  nurseFocusTemplates: [],
  nurseNotes: [],
  nurseSavedMessage: "",
  nurseTemplateError: "",
  nurseNoteHeadId: null,
  allergyItems: [],
  problemItems: [],
  member: null,
  nextAppointment: "",
  channels: [],
  teamMessageErrors: [],
  medications: [],
  coverage: "",
  panelLoaded: false,
  checkout: null,
});
